import { useState, useEffect, useRef } from "react";

const CATEGORIES = [
  {
    id: "write",
    icon: "✍️",
    label: "Write & Generate Content",
    description: "Blog posts, emails, copy, scripts",
    subcategories: ["Blog & Articles", "Marketing Copy", "Email Writing", "Social Media", "Scripts & Storytelling"]
  },
  {
    id: "images",
    icon: "🎨",
    label: "Generate Images & Video",
    description: "Art, logos, product shots, video",
    subcategories: ["Photorealistic Images", "Illustrations & Art", "Logos & Branding", "Video Generation", "Image Editing"]
  },
  {
    id: "analyse",
    icon: "🔍",
    label: "Analyse & Summarise",
    description: "Documents, data, research, PDFs",
    subcategories: ["PDF & Documents", "Data & Spreadsheets", "Research Summaries", "Legal & Contracts", "Financial Reports"]
  },
  {
    id: "automate",
    icon: "⚡",
    label: "Automate Tasks",
    description: "Workflows, scheduling, integrations",
    subcategories: ["App Integrations", "Email Automation", "Social Scheduling", "Data Entry", "Reporting"]
  },
  {
    id: "build",
    icon: "🛠️",
    label: "Build Something",
    description: "Code, websites, apps, tools",
    subcategories: ["Write Code", "Build Websites", "Debug & Review Code", "Databases", "APIs"]
  },
  {
    id: "research",
    icon: "🧠",
    label: "Research & Find Info",
    description: "Web research, fact-checking, sourcing",
    subcategories: ["Web Research", "Competitor Analysis", "Market Research", "Academic Research", "News & Trends"]
  },
  {
    id: "business",
    icon: "💼",
    label: "Run a Business",
    description: "Admin, CRM, finance, hiring",
    subcategories: ["Customer Support", "Invoicing & Finance", "HR & Hiring", "Project Management", "Meeting Notes"]
  },
  {
    id: "learn",
    icon: "📚",
    label: "Learn a Skill",
    description: "Courses, tutoring, practice, quizzes",
    subcategories: ["Language Learning", "Coding Bootcamp", "Creative Skills", "Business Skills", "AI Literacy"]
  },
  {
    id: "audio",
    icon: "🎙️",
    label: "Audio & Voice",
    description: "Transcription, voice synthesis, podcasts",
    subcategories: ["Transcription", "Text to Speech", "Podcast Tools", "Voice Cloning", "Translation"]
  },
  {
    id: "video",
    icon: "🎬",
    label: "Edit & Create Video",
    description: "Editing, captions, clips, effects, YouTube",
    subcategories: ["Video Editing", "Auto Captions & Subtitles", "Short Clip Creation", "Screen Recording", "YouTube Tools"]
  },
  {
    id: "presentations",
    icon: "📊",
    label: "Presentations & Docs",
    description: "Slide decks, reports, proposals, pitch decks",
    subcategories: ["Slide Decks", "Pitch Decks", "Business Reports", "Proposals & Quotes", "Whiteboards & Diagrams"]
  },
  {
    id: "seo",
    icon: "📈",
    label: "SEO & Website Growth",
    description: "Rankings, keywords, backlinks, traffic",
    subcategories: ["Keyword Research", "On-Page SEO", "Backlink Analysis", "Technical SEO", "Content Optimisation"]
  },
  {
    id: "ecommerce",
    icon: "🛒",
    label: "Sell Online",
    description: "Stores, product listings, pricing, ads",
    subcategories: ["Build an Online Store", "Product Descriptions", "Pricing & Inventory", "Ad Campaigns", "Customer Reviews & UGC"]
  },
  {
    id: "customerresearch",
    icon: "👥",
    label: "Understand Customers",
    description: "Surveys, feedback, personas, interviews",
    subcategories: ["Surveys & Feedback", "User Interviews", "Customer Personas", "Sentiment Analysis", "Churn Analysis"]
  }
];

const TOOLS_DB = {
  write: {
    "Blog & Articles": [
      { name: "Claude", tagline: "Best for long-form, nuanced writing", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium", "beginner-friendly"] },
      { name: "Jasper", tagline: "Marketing-focused AI writer with templates", price: "From $49/mo", rating: 4.6, badge: "Best for Teams", url: "https://jasper.ai", tags: ["paid", "teams"] },
      { name: "Copy.ai", tagline: "Fast drafts with 90+ content templates", price: "Free / $49mo", rating: 4.5, badge: null, url: "https://copy.ai", tags: ["freemium"] },
      { name: "Writesonic", tagline: "SEO-optimised articles and blog content", price: "Free / $19mo", rating: 4.4, badge: "Best for SEO", url: "https://writesonic.com", tags: ["freemium", "seo"] },
    ],
    "Marketing Copy": [
      { name: "Copy.ai", tagline: "Purpose-built for conversion copy", price: "Free / $49mo", rating: 4.7, badge: "Top Pick", url: "https://copy.ai", tags: ["freemium"] },
      { name: "Jasper", tagline: "Brand voice training + ad copy templates", price: "From $49/mo", rating: 4.6, badge: null, url: "https://jasper.ai", tags: ["paid"] },
      { name: "Claude", tagline: "Nuanced, persuasive copy on demand", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Email Writing": [
      { name: "Superhuman AI", tagline: "AI email drafts inside your inbox", price: "$30/mo", rating: 4.7, badge: "Top Pick", url: "https://superhuman.com", tags: ["paid"] },
      { name: "Claude", tagline: "Tone-perfect emails for any situation", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
      { name: "Lavender", tagline: "Sales email coach with real-time scoring", price: "Free / $29mo", rating: 4.5, badge: "Best for Sales", url: "https://lavender.ai", tags: ["freemium"] },
    ],
    "Social Media": [
      { name: "Buffer AI", tagline: "Generate + schedule posts across platforms", price: "Free / $18mo", rating: 4.5, badge: "Top Pick", url: "https://buffer.com", tags: ["freemium"] },
      { name: "Lately", tagline: "Repurposes long content into social posts", price: "From $49/mo", rating: 4.3, badge: null, url: "https://lately.ai", tags: ["paid"] },
      { name: "Taplio", tagline: "LinkedIn-focused content and scheduling", price: "From $39/mo", rating: 4.4, badge: "Best for LinkedIn", url: "https://taplio.com", tags: ["paid"] },
    ],
    "Scripts & Storytelling": [
      { name: "Claude", tagline: "Rich narrative, dialogue, and structure", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium"] },
      { name: "Sudowrite", tagline: "Fiction-first writing assistant", price: "From $19/mo", rating: 4.6, badge: "Best for Fiction", url: "https://sudowrite.com", tags: ["paid"] },
      { name: "ChatGPT", tagline: "Versatile creative writing partner", price: "Free / $20mo", rating: 4.5, badge: null, url: "https://chatgpt.com", tags: ["freemium"] },
    ]
  },
  images: {
    "Photorealistic Images": [
      { name: "Midjourney", tagline: "The gold standard for stunning AI imagery", price: "From $10/mo", rating: 4.9, badge: "Top Pick", url: "https://midjourney.com", tags: ["paid"] },
      { name: "DALL·E 3", tagline: "Prompt-accurate photorealism via ChatGPT", price: "Free / $20mo", rating: 4.6, badge: null, url: "https://chatgpt.com", tags: ["freemium"] },
      { name: "Ideogram", tagline: "Great text-in-image generation", price: "Free / $8mo", rating: 4.5, badge: "Best Free Option", url: "https://ideogram.ai", tags: ["freemium"] },
    ],
    "Illustrations & Art": [
      { name: "Midjourney", tagline: "Unmatched artistic styles and aesthetics", price: "From $10/mo", rating: 4.9, badge: "Top Pick", url: "https://midjourney.com", tags: ["paid"] },
      { name: "Adobe Firefly", tagline: "Commercial-safe art and illustration", price: "Free / $5mo", rating: 4.5, badge: "Commercially Safe", url: "https://firefly.adobe.com", tags: ["freemium"] },
      { name: "Leonardo AI", tagline: "Fine-tuned models for consistent style", price: "Free / $12mo", rating: 4.6, badge: null, url: "https://leonardo.ai", tags: ["freemium"] },
    ],
    "Logos & Branding": [
      { name: "Looka", tagline: "AI logo maker with brand kit output", price: "One-time $65+", rating: 4.4, badge: "Top Pick", url: "https://looka.com", tags: ["paid"] },
      { name: "Adobe Firefly", tagline: "Vector-ready commercial brand assets", price: "Free / $5mo", rating: 4.5, badge: null, url: "https://firefly.adobe.com", tags: ["freemium"] },
      { name: "Canva AI", tagline: "Logo + full brand template generation", price: "Free / $15mo", rating: 4.5, badge: "Easiest to Use", url: "https://canva.com", tags: ["freemium", "beginner-friendly"] },
    ],
    "Video Generation": [
      { name: "Sora", tagline: "OpenAI's text-to-video, cinematic quality", price: "$20/mo (ChatGPT Pro)", rating: 4.7, badge: "Top Pick", url: "https://sora.com", tags: ["paid"] },
      { name: "Runway ML", tagline: "Pro video editing + AI generation", price: "Free / $15mo", rating: 4.6, badge: "Best for Editors", url: "https://runwayml.com", tags: ["freemium"] },
      { name: "Kling AI", tagline: "High motion quality, realistic video", price: "Free / $10mo", rating: 4.5, badge: null, url: "https://klingai.com", tags: ["freemium"] },
    ],
    "Image Editing": [
      { name: "Adobe Firefly", tagline: "Generative fill, remove, and retouch", price: "Free / $5mo", rating: 4.7, badge: "Top Pick", url: "https://firefly.adobe.com", tags: ["freemium"] },
      { name: "Clipdrop", tagline: "Background removal, upscaling, cleanup", price: "Free / $13mo", rating: 4.5, badge: null, url: "https://clipdrop.co", tags: ["freemium"] },
      { name: "Canva AI", tagline: "Magic edit inside a design tool", price: "Free / $15mo", rating: 4.4, badge: "Easiest to Use", url: "https://canva.com", tags: ["freemium", "beginner-friendly"] },
    ]
  },
  analyse: {
    "PDF & Documents": [
      { name: "Claude", tagline: "Reads entire PDFs, answers questions precisely", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium"] },
      { name: "ChatPDF", tagline: "Dedicated PDF chat interface", price: "Free / $5mo", rating: 4.5, badge: null, url: "https://chatpdf.com", tags: ["freemium"] },
      { name: "Humata AI", tagline: "Enterprise doc analysis and Q&A", price: "Free / $20mo", rating: 4.4, badge: "Best for Teams", url: "https://humata.ai", tags: ["freemium"] },
    ],
    "Data & Spreadsheets": [
      { name: "Julius AI", tagline: "Chat with your CSV and Excel data", price: "Free / $20mo", rating: 4.6, badge: "Top Pick", url: "https://julius.ai", tags: ["freemium"] },
      { name: "ChatGPT + Code Interpreter", tagline: "Upload data, get instant analysis + charts", price: "$20/mo", rating: 4.7, badge: null, url: "https://chatgpt.com", tags: ["paid"] },
      { name: "Rows AI", tagline: "AI-powered spreadsheet with built-in analysis", price: "Free / $59mo", rating: 4.4, badge: null, url: "https://rows.com", tags: ["freemium"] },
    ],
    "Research Summaries": [
      { name: "Perplexity AI", tagline: "Cited, real-time research summaries", price: "Free / $20mo", rating: 4.8, badge: "Top Pick", url: "https://perplexity.ai", tags: ["freemium"] },
      { name: "Claude", tagline: "Deep synthesis of complex topics", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
      { name: "Elicit", tagline: "Academic paper research and extraction", price: "Free / $10mo", rating: 4.5, badge: "Best for Academic", url: "https://elicit.org", tags: ["freemium"] },
    ],
    "Legal & Contracts": [
      { name: "Harvey AI", tagline: "Enterprise legal AI used by top firms", price: "Custom pricing", rating: 4.7, badge: "Top Pick", url: "https://harvey.ai", tags: ["enterprise"] },
      { name: "Spellbook", tagline: "Contract review and drafting in Word", price: "From $99/mo", rating: 4.5, badge: null, url: "https://spellbook.legal", tags: ["paid"] },
      { name: "Claude", tagline: "Summarise and explain legal documents", price: "Free / $20mo", rating: 4.6, badge: "Best Value", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Financial Reports": [
      { name: "Domo AI", tagline: "BI + AI for financial dashboards", price: "Custom", rating: 4.4, badge: "Top Pick", url: "https://domo.com", tags: ["enterprise"] },
      { name: "ChatGPT + Code Interpreter", tagline: "Analyse financials from uploaded reports", price: "$20/mo", rating: 4.6, badge: null, url: "https://chatgpt.com", tags: ["paid"] },
      { name: "Polygon AI", tagline: "Real-time financial data + AI insights", price: "Free / $29mo", rating: 4.3, badge: null, url: "https://polygon.io", tags: ["freemium"] },
    ]
  },
  automate: {
    "App Integrations": [
      { name: "Zapier", tagline: "Connect 6,000+ apps without code", price: "Free / $20mo", rating: 4.8, badge: "Top Pick", url: "https://zapier.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Make (Integromat)", tagline: "Visual workflow builder, more powerful", price: "Free / $9mo", rating: 4.7, badge: "Most Powerful", url: "https://make.com", tags: ["freemium"] },
      { name: "n8n", tagline: "Open-source, self-hostable automation", price: "Free / $24mo", rating: 4.6, badge: "Best for Devs", url: "https://n8n.io", tags: ["freemium"] },
    ],
    "Email Automation": [
      { name: "ActiveCampaign", tagline: "Advanced email sequences and CRM", price: "From $15/mo", rating: 4.6, badge: "Top Pick", url: "https://activecampaign.com", tags: ["paid"] },
      { name: "Mailchimp AI", tagline: "Simple automation for small businesses", price: "Free / $13mo", rating: 4.4, badge: "Easiest to Start", url: "https://mailchimp.com", tags: ["freemium"] },
      { name: "Instantly", tagline: "Cold email automation at scale", price: "From $37/mo", rating: 4.5, badge: "Best for Outreach", url: "https://instantly.ai", tags: ["paid"] },
    ],
    "Social Scheduling": [
      { name: "Buffer", tagline: "Clean, simple cross-platform scheduling", price: "Free / $18mo", rating: 4.6, badge: "Top Pick", url: "https://buffer.com", tags: ["freemium"] },
      { name: "Publer", tagline: "AI captions + scheduling + analytics", price: "Free / $12mo", rating: 4.5, badge: null, url: "https://publer.io", tags: ["freemium"] },
      { name: "Hootsuite", tagline: "Enterprise social media management", price: "From $99/mo", rating: 4.3, badge: "Best for Enterprises", url: "https://hootsuite.com", tags: ["paid"] },
    ],
    "Data Entry": [
      { name: "Bardeen", tagline: "Automate web scraping and data entry", price: "Free / $10mo", rating: 4.5, badge: "Top Pick", url: "https://bardeen.ai", tags: ["freemium"] },
      { name: "Nanonets", tagline: "AI extracts data from forms and docs", price: "Free / $49mo", rating: 4.4, badge: null, url: "https://nanonets.com", tags: ["freemium"] },
      { name: "Zapier Tables", tagline: "Automated data capture and routing", price: "Free / $20mo", rating: 4.3, badge: null, url: "https://zapier.com", tags: ["freemium"] },
    ],
    "Reporting": [
      { name: "Databox", tagline: "Pull data from 100+ sources into dashboards", price: "Free / $47mo", rating: 4.5, badge: "Top Pick", url: "https://databox.com", tags: ["freemium"] },
      { name: "Looker Studio", tagline: "Free Google reporting with AI summaries", price: "Free", rating: 4.4, badge: "Best Free Option", url: "https://lookerstudio.google.com", tags: ["free"] },
      { name: "Polymer", tagline: "Upload data, AI builds the dashboard", price: "Free / $10mo", rating: 4.3, badge: null, url: "https://polymersearch.com", tags: ["freemium"] },
    ]
  },
  build: {
    "Write Code": [
      { name: "GitHub Copilot", tagline: "AI code completion inside your editor", price: "Free / $10mo", rating: 4.8, badge: "Top Pick", url: "https://github.com/copilot", tags: ["freemium"] },
      { name: "Claude", tagline: "Explains, writes and debugs any language", price: "Free / $20mo", rating: 4.9, badge: "Best for Complex Code", url: "https://claude.ai", tags: ["freemium"] },
      { name: "Cursor", tagline: "AI-native code editor", price: "Free / $20mo", rating: 4.7, badge: null, url: "https://cursor.sh", tags: ["freemium"] },
    ],
    "Build Websites": [
      { name: "Bolt.new", tagline: "Describe a site, get full working code", price: "Free / $20mo", rating: 4.7, badge: "Top Pick", url: "https://bolt.new", tags: ["freemium"] },
      { name: "v0 by Vercel", tagline: "AI generates React UI components", price: "Free / $20mo", rating: 4.6, badge: null, url: "https://v0.dev", tags: ["freemium"] },
      { name: "Framer AI", tagline: "Design + publish websites via AI prompts", price: "Free / $15mo", rating: 4.5, badge: "Best No-Code", url: "https://framer.com", tags: ["freemium"] },
    ],
    "Debug & Review Code": [
      { name: "Claude", tagline: "Pinpoints bugs and explains fixes clearly", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium"] },
      { name: "CodeRabbit", tagline: "Automated PR reviews with AI comments", price: "Free / $12mo", rating: 4.6, badge: "Best for Teams", url: "https://coderabbit.ai", tags: ["freemium"] },
      { name: "Sourcery", tagline: "Python and JS code quality improvement", price: "Free / $19mo", rating: 4.4, badge: null, url: "https://sourcery.ai", tags: ["freemium"] },
    ],
    "Databases": [
      { name: "Airtable AI", tagline: "AI-enhanced no-code database", price: "Free / $20mo", rating: 4.6, badge: "Top Pick", url: "https://airtable.com", tags: ["freemium"] },
      { name: "Supabase", tagline: "Open-source backend with AI tools", price: "Free / $25mo", rating: 4.7, badge: "Best for Devs", url: "https://supabase.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Write and explain SQL queries from English", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
    ],
    "APIs": [
      { name: "Postman AI", tagline: "API testing and documentation assistant", price: "Free / $14mo", rating: 4.6, badge: "Top Pick", url: "https://postman.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Generate API integration code from docs", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
      { name: "RapidAPI", tagline: "Browse and connect 40,000+ APIs", price: "Free / varies", rating: 4.4, badge: "Best Directory", url: "https://rapidapi.com", tags: ["freemium"] },
    ]
  },
  research: {
    "Web Research": [
      { name: "Perplexity AI", tagline: "Real-time cited answers from the web", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://perplexity.ai", tags: ["freemium"] },
      { name: "Claude", tagline: "Deep analysis with web search built in", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
      { name: "You.com", tagline: "Search engine with AI chat mode", price: "Free / $20mo", rating: 4.4, badge: "Good Free Option", url: "https://you.com", tags: ["freemium"] },
    ],
    "Competitor Analysis": [
      { name: "Semrush", tagline: "Deep competitor SEO, ads, and content data", price: "From $120/mo", rating: 4.7, badge: "Top Pick", url: "https://semrush.com", tags: ["paid"] },
      { name: "Similarweb", tagline: "Traffic and audience comparison tool", price: "Free / $125mo", rating: 4.6, badge: null, url: "https://similarweb.com", tags: ["freemium"] },
      { name: "SpyFu", tagline: "See competitor keywords and ad spend", price: "From $39/mo", rating: 4.4, badge: "Best Value", url: "https://spyfu.com", tags: ["paid"] },
    ],
    "Market Research": [
      { name: "Exploding Topics", tagline: "Find trends before they go mainstream", price: "Free / $39mo", rating: 4.6, badge: "Top Pick", url: "https://explodingtopics.com", tags: ["freemium"] },
      { name: "Glimpse", tagline: "Google Trends on steroids", price: "From $99/mo", rating: 4.5, badge: null, url: "https://meetglimpse.com", tags: ["paid"] },
      { name: "Perplexity AI", tagline: "Instant market overview with sources", price: "Free / $20mo", rating: 4.7, badge: "Best for Quick Research", url: "https://perplexity.ai", tags: ["freemium"] },
    ],
    "Academic Research": [
      { name: "Elicit", tagline: "Find and extract data from papers", price: "Free / $10mo", rating: 4.7, badge: "Top Pick", url: "https://elicit.org", tags: ["freemium"] },
      { name: "Consensus", tagline: "AI search engine for scientific papers", price: "Free / $9mo", rating: 4.5, badge: null, url: "https://consensus.app", tags: ["freemium"] },
      { name: "Claude", tagline: "Synthesise and explain academic content", price: "Free / $20mo", rating: 4.7, badge: null, url: "https://claude.ai", tags: ["freemium"] },
    ],
    "News & Trends": [
      { name: "Perplexity AI", tagline: "Live news with source attribution", price: "Free / $20mo", rating: 4.8, badge: "Top Pick", url: "https://perplexity.ai", tags: ["freemium"] },
      { name: "Feedly AI", tagline: "AI-curated news feeds by topic", price: "Free / $8mo", rating: 4.4, badge: null, url: "https://feedly.com", tags: ["freemium"] },
      { name: "Exploding Topics", tagline: "Spot rising trends early", price: "Free / $39mo", rating: 4.5, badge: "Best for Trends", url: "https://explodingtopics.com", tags: ["freemium"] },
    ]
  },
  business: {
    "Customer Support": [
      { name: "Intercom Fin", tagline: "AI agent that resolves 50%+ of tickets", price: "From $74/mo", rating: 4.7, badge: "Top Pick", url: "https://intercom.com", tags: ["paid"] },
      { name: "Tidio", tagline: "AI chatbot for small business support", price: "Free / $29mo", rating: 4.5, badge: "Best Value", url: "https://tidio.com", tags: ["freemium"] },
      { name: "Zendesk AI", tagline: "Enterprise support with AI triage", price: "From $55/mo", rating: 4.5, badge: "Best Enterprise", url: "https://zendesk.com", tags: ["paid"] },
    ],
    "Invoicing & Finance": [
      { name: "FreshBooks", tagline: "AI-assisted invoicing and expense tracking", price: "From $17/mo", rating: 4.6, badge: "Top Pick", url: "https://freshbooks.com", tags: ["paid"] },
      { name: "Wave", tagline: "Free accounting for small businesses", price: "Free / $16mo", rating: 4.4, badge: "Best Free Option", url: "https://waveapps.com", tags: ["freemium"] },
      { name: "Xero", tagline: "Powerful AI bookkeeping for SMBs", price: "From $15/mo", rating: 4.6, badge: null, url: "https://xero.com", tags: ["paid"] },
    ],
    "HR & Hiring": [
      { name: "Workable", tagline: "AI-powered job posts and candidate ranking", price: "From $149/mo", rating: 4.5, badge: "Top Pick", url: "https://workable.com", tags: ["paid"] },
      { name: "Manatal", tagline: "AI recruitment for small teams", price: "From $15/mo", rating: 4.5, badge: "Best Value", url: "https://manatal.com", tags: ["paid"] },
      { name: "Claude", tagline: "Write job descriptions and interview questions", price: "Free / $20mo", rating: 4.7, badge: "Best for Solo Hiring", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Project Management": [
      { name: "Notion AI", tagline: "Docs + tasks + AI in one workspace", price: "Free / $10mo", rating: 4.7, badge: "Top Pick", url: "https://notion.so", tags: ["freemium"] },
      { name: "ClickUp AI", tagline: "Full PM suite with AI writing and summaries", price: "Free / $7mo", rating: 4.6, badge: null, url: "https://clickup.com", tags: ["freemium"] },
      { name: "Linear", tagline: "Fast, minimal project tracking for teams", price: "Free / $8mo", rating: 4.7, badge: "Best for Dev Teams", url: "https://linear.app", tags: ["freemium"] },
    ],
    "Meeting Notes": [
      { name: "Otter.ai", tagline: "Live transcription and meeting summaries", price: "Free / $10mo", rating: 4.6, badge: "Top Pick", url: "https://otter.ai", tags: ["freemium"] },
      { name: "Fireflies.ai", tagline: "Records, transcribes, and summarises calls", price: "Free / $10mo", rating: 4.6, badge: null, url: "https://fireflies.ai", tags: ["freemium"] },
      { name: "Fathom", tagline: "Free unlimited call recording and notes", price: "Free / $15mo", rating: 4.7, badge: "Best Free Option", url: "https://fathom.video", tags: ["freemium", "free"] },
    ]
  },
  learn: {
    "Language Learning": [
      { name: "Duolingo Max", tagline: "AI conversations and explanations built in", price: "Free / $14mo", rating: 4.6, badge: "Top Pick", url: "https://duolingo.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Speak", tagline: "AI speaking partner for fluency practice", price: "From $9/mo", rating: 4.5, badge: "Best for Speaking", url: "https://speak.com", tags: ["paid"] },
      { name: "Claude", tagline: "Conversational practice in any language", price: "Free / $20mo", rating: 4.7, badge: null, url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Coding Bootcamp": [
      { name: "Codecademy AI", tagline: "Interactive coding with AI feedback", price: "Free / $17mo", rating: 4.5, badge: "Top Pick", url: "https://codecademy.com", tags: ["freemium"] },
      { name: "GitHub Copilot", tagline: "Learn by coding with AI assistance", price: "Free / $10mo", rating: 4.7, badge: null, url: "https://github.com/copilot", tags: ["freemium"] },
      { name: "Claude", tagline: "Ask anything, get explained line by line", price: "Free / $20mo", rating: 4.9, badge: "Best Tutor", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Creative Skills": [
      { name: "Skillshare", tagline: "Thousands of creative courses with AI tools", price: "From $14/mo", rating: 4.4, badge: "Top Pick", url: "https://skillshare.com", tags: ["paid"] },
      { name: "Adobe Firefly", tagline: "Learn design by creating with AI", price: "Free / $5mo", rating: 4.5, badge: null, url: "https://firefly.adobe.com", tags: ["freemium"] },
      { name: "Canva Design School", tagline: "Free design courses + hands-on tools", price: "Free", rating: 4.4, badge: "Best Free Option", url: "https://canva.com/designschool", tags: ["free"] },
    ],
    "Business Skills": [
      { name: "Coursera", tagline: "University-backed business courses", price: "Free / $49mo", rating: 4.5, badge: "Top Pick", url: "https://coursera.org", tags: ["freemium"] },
      { name: "Maven", tagline: "Live cohort courses from top practitioners", price: "Varies", rating: 4.6, badge: "Best for Networking", url: "https://maven.com", tags: ["paid"] },
      { name: "Claude", tagline: "On-demand business tutor for any topic", price: "Free / $20mo", rating: 4.8, badge: null, url: "https://claude.ai", tags: ["freemium"] },
    ],
    "AI Literacy": [
      { name: "DeepLearning.AI", tagline: "Best AI courses from Andrew Ng", price: "Free / $49mo", rating: 4.8, badge: "Top Pick", url: "https://deeplearning.ai", tags: ["freemium"] },
      { name: "Coursera AI Courses", tagline: "Structured AI certificates", price: "Free / $49mo", rating: 4.5, badge: null, url: "https://coursera.org", tags: ["freemium"] },
      { name: "Claude", tagline: "Ask and learn AI concepts conversationally", price: "Free / $20mo", rating: 4.7, badge: "Best Hands-On", url: "https://claude.ai", tags: ["freemium"] },
    ]
  },
  audio: {
    "Transcription": [
      { name: "Whisper / OpenAI", tagline: "Industry-best accuracy, many languages", price: "Free (open source)", rating: 4.8, badge: "Top Pick", url: "https://openai.com/research/whisper", tags: ["free"] },
      { name: "Otter.ai", tagline: "Real-time transcription with speaker labels", price: "Free / $10mo", rating: 4.6, badge: "Best for Meetings", url: "https://otter.ai", tags: ["freemium"] },
      { name: "Descript", tagline: "Transcription + edit audio by editing text", price: "Free / $12mo", rating: 4.6, badge: null, url: "https://descript.com", tags: ["freemium"] },
    ],
    "Text to Speech": [
      { name: "ElevenLabs", tagline: "Most realistic AI voice synthesis available", price: "Free / $5mo", rating: 4.9, badge: "Top Pick", url: "https://elevenlabs.io", tags: ["freemium"] },
      { name: "Murf AI", tagline: "Studio-quality voiceovers with 120+ voices", price: "Free / $19mo", rating: 4.6, badge: null, url: "https://murf.ai", tags: ["freemium"] },
      { name: "Play.ht", tagline: "Ultra-realistic cloned voices", price: "Free / $31mo", rating: 4.5, badge: null, url: "https://play.ht", tags: ["freemium"] },
    ],
    "Podcast Tools": [
      { name: "Descript", tagline: "Edit podcasts like a Word doc", price: "Free / $12mo", rating: 4.7, badge: "Top Pick", url: "https://descript.com", tags: ["freemium"] },
      { name: "Riverside.fm", tagline: "Record + AI edit + transcribe podcasts", price: "Free / $15mo", rating: 4.6, badge: null, url: "https://riverside.fm", tags: ["freemium"] },
      { name: "Podcastle", tagline: "AI audio cleanup and show notes", price: "Free / $12mo", rating: 4.4, badge: "Best for Beginners", url: "https://podcastle.ai", tags: ["freemium"] },
    ],
    "Voice Cloning": [
      { name: "ElevenLabs", tagline: "Clone a voice from 1 minute of audio", price: "Free / $5mo", rating: 4.9, badge: "Top Pick", url: "https://elevenlabs.io", tags: ["freemium"] },
      { name: "Resemble AI", tagline: "Enterprise-grade custom voice models", price: "From $29/mo", rating: 4.5, badge: "Best Enterprise", url: "https://resemble.ai", tags: ["paid"] },
      { name: "Play.ht", tagline: "Instant voice cloning with API access", price: "Free / $31mo", rating: 4.5, badge: null, url: "https://play.ht", tags: ["freemium"] },
    ],
    "Translation": [
      { name: "DeepL", tagline: "Most accurate AI translation available", price: "Free / $8mo", rating: 4.8, badge: "Top Pick", url: "https://deepl.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Contextual translation preserving tone", price: "Free / $20mo", rating: 4.7, badge: null, url: "https://claude.ai", tags: ["freemium"] },
      { name: "Whisper + ElevenLabs", tagline: "Transcribe + translate + re-voice audio", price: "Free / $5mo", rating: 4.6, badge: "Best for Audio", url: "https://elevenlabs.io", tags: ["freemium"] },
    ]
  },
  video: {
    "Video Editing": [
      { name: "Descript", tagline: "Edit video by editing the transcript like a doc", price: "Free / $12mo", rating: 4.8, badge: "Top Pick", url: "https://descript.com", tags: ["freemium", "beginner-friendly"] },
      { name: "CapCut", tagline: "Fast AI-powered editing, huge template library", price: "Free / $8mo", rating: 4.6, badge: "Best Free Option", url: "https://capcut.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Runway ML", tagline: "Pro-grade AI editing and generative effects", price: "Free / $15mo", rating: 4.7, badge: "Most Powerful", url: "https://runwayml.com", tags: ["freemium"] },
      { name: "Adobe Premiere Pro AI", tagline: "Industry standard with AI scene editing", price: "From $35/mo", rating: 4.6, badge: "Best Enterprise", url: "https://adobe.com/premiere", tags: ["paid"] },
    ],
    "Auto Captions & Subtitles": [
      { name: "Captions.ai", tagline: "Automatic styled captions for social video", price: "Free / $8mo", rating: 4.7, badge: "Top Pick", url: "https://captions.ai", tags: ["freemium", "beginner-friendly"] },
      { name: "Submagic", tagline: "Viral-style animated captions in seconds", price: "From $20/mo", rating: 4.6, badge: "Best for Social", url: "https://submagic.co", tags: ["paid"] },
      { name: "Descript", tagline: "Captions auto-generated from transcript", price: "Free / $12mo", rating: 4.6, badge: null, url: "https://descript.com", tags: ["freemium"] },
    ],
    "Short Clip Creation": [
      { name: "Opus Clip", tagline: "Turns long videos into viral short clips with AI", price: "Free / $15mo", rating: 4.8, badge: "Top Pick", url: "https://opus.pro", tags: ["freemium"] },
      { name: "Munch", tagline: "AI picks the best moments from long content", price: "From $49/mo", rating: 4.5, badge: null, url: "https://getmunch.com", tags: ["paid"] },
      { name: "CapCut", tagline: "Auto-highlights and clip templates", price: "Free / $8mo", rating: 4.5, badge: "Easiest to Use", url: "https://capcut.com", tags: ["freemium", "beginner-friendly"] },
    ],
    "Screen Recording": [
      { name: "Loom", tagline: "Record screen + cam, share instantly with AI summary", price: "Free / $15mo", rating: 4.8, badge: "Top Pick", url: "https://loom.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Tella", tagline: "Beautiful screen recordings with AI editing", price: "Free / $19mo", rating: 4.6, badge: null, url: "https://tella.tv", tags: ["freemium"] },
      { name: "Screenpal", tagline: "Simple recording with AI captions and trim", price: "Free / $4mo", rating: 4.3, badge: "Best Value", url: "https://screenpal.com", tags: ["freemium"] },
    ],
    "YouTube Tools": [
      { name: "TubeBuddy", tagline: "SEO, A/B thumbnails, and channel analytics", price: "Free / $6mo", rating: 4.6, badge: "Top Pick", url: "https://tubebuddy.com", tags: ["freemium"] },
      { name: "VidIQ", tagline: "AI title, description, and keyword suggestions", price: "Free / $10mo", rating: 4.5, badge: null, url: "https://vidiq.com", tags: ["freemium"] },
      { name: "Opus Clip", tagline: "Repurpose YouTube videos into shorts automatically", price: "Free / $15mo", rating: 4.6, badge: "Best for Repurposing", url: "https://opus.pro", tags: ["freemium"] },
    ]
  },
  presentations: {
    "Slide Decks": [
      { name: "Gamma", tagline: "Type a prompt, get a beautiful slide deck instantly", price: "Free / $10mo", rating: 4.8, badge: "Top Pick", url: "https://gamma.app", tags: ["freemium", "beginner-friendly"] },
      { name: "Beautiful.ai", tagline: "Smart slides that design themselves", price: "From $12mo", rating: 4.6, badge: null, url: "https://beautiful.ai", tags: ["paid"] },
      { name: "Canva AI", tagline: "Huge template library with AI presentation tools", price: "Free / $15mo", rating: 4.5, badge: "Easiest to Use", url: "https://canva.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Tome", tagline: "Narrative-focused AI presentation builder", price: "Free / $20mo", rating: 4.5, badge: null, url: "https://tome.app", tags: ["freemium"] },
    ],
    "Pitch Decks": [
      { name: "Gamma", tagline: "Investor-ready decks from a one-line prompt", price: "Free / $10mo", rating: 4.7, badge: "Top Pick", url: "https://gamma.app", tags: ["freemium"] },
      { name: "Slidebean", tagline: "Startup pitch deck templates loved by VCs", price: "Free / $29mo", rating: 4.5, badge: "Best for Startups", url: "https://slidebean.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Write the narrative and content for any pitch", price: "Free / $20mo", rating: 4.8, badge: "Best for Story", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Business Reports": [
      { name: "Claude", tagline: "Generate structured reports from data or briefs", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium"] },
      { name: "Gamma", tagline: "Turn reports into visual documents instantly", price: "Free / $10mo", rating: 4.6, badge: null, url: "https://gamma.app", tags: ["freemium"] },
      { name: "Notion AI", tagline: "Write and format reports inside your workspace", price: "Free / $10mo", rating: 4.6, badge: "Best for Teams", url: "https://notion.so", tags: ["freemium"] },
    ],
    "Proposals & Quotes": [
      { name: "PandaDoc", tagline: "AI-assisted proposals with e-signature built in", price: "Free / $35mo", rating: 4.7, badge: "Top Pick", url: "https://pandadoc.com", tags: ["freemium"] },
      { name: "Better Proposals", tagline: "High-converting proposal templates with tracking", price: "From $19mo", rating: 4.5, badge: null, url: "https://betterproposals.io", tags: ["paid"] },
      { name: "Claude", tagline: "Draft custom proposals from a short brief", price: "Free / $20mo", rating: 4.8, badge: "Best Value", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Whiteboards & Diagrams": [
      { name: "Miro AI", tagline: "Collaborative whiteboard with AI summarisation", price: "Free / $10mo", rating: 4.7, badge: "Top Pick", url: "https://miro.com", tags: ["freemium"] },
      { name: "FigJam AI", tagline: "Figma's whiteboard with AI sticky note clustering", price: "Free / $5mo", rating: 4.6, badge: "Best for Designers", url: "https://figma.com/figjam", tags: ["freemium"] },
      { name: "Whimsical", tagline: "Fast flowcharts, mind maps, and wireframes", price: "Free / $10mo", rating: 4.5, badge: null, url: "https://whimsical.com", tags: ["freemium"] },
    ]
  },
  seo: {
    "Keyword Research": [
      { name: "Ahrefs", tagline: "The deepest keyword and competitor data available", price: "From $99/mo", rating: 4.9, badge: "Top Pick", url: "https://ahrefs.com", tags: ["paid"] },
      { name: "Semrush", tagline: "All-in-one SEO suite with strong keyword tools", price: "From $120/mo", rating: 4.7, badge: "Most Comprehensive", url: "https://semrush.com", tags: ["paid"] },
      { name: "Ubersuggest", tagline: "Affordable keyword research for small businesses", price: "Free / $12mo", rating: 4.4, badge: "Best Value", url: "https://neilpatel.com/ubersuggest", tags: ["freemium"] },
      { name: "Google Keyword Planner", tagline: "Free keyword data straight from Google", price: "Free", rating: 4.3, badge: "Best Free Option", url: "https://ads.google.com/home/tools/keyword-planner", tags: ["free"] },
    ],
    "On-Page SEO": [
      { name: "Surfer SEO", tagline: "AI content editor that scores pages in real-time", price: "From $89/mo", rating: 4.7, badge: "Top Pick", url: "https://surferseo.com", tags: ["paid"] },
      { name: "Clearscope", tagline: "Content optimisation trusted by major publishers", price: "From $170/mo", rating: 4.6, badge: "Best for Agencies", url: "https://clearscope.io", tags: ["paid"] },
      { name: "NeuronWriter", tagline: "Affordable Surfer alternative with AI writing", price: "From $19/mo", rating: 4.4, badge: "Best Value", url: "https://neuronwriter.com", tags: ["paid"] },
    ],
    "Backlink Analysis": [
      { name: "Ahrefs", tagline: "Largest backlink index in the industry", price: "From $99/mo", rating: 4.9, badge: "Top Pick", url: "https://ahrefs.com", tags: ["paid"] },
      { name: "Semrush", tagline: "Backlink audit and toxic link detection", price: "From $120/mo", rating: 4.6, badge: null, url: "https://semrush.com", tags: ["paid"] },
      { name: "Moz Link Explorer", tagline: "Free backlink checker with domain authority", price: "Free / $99mo", rating: 4.4, badge: "Best Free Option", url: "https://moz.com/link-explorer", tags: ["freemium"] },
    ],
    "Technical SEO": [
      { name: "Screaming Frog", tagline: "Deep site crawl for technical issues", price: "Free / $259/yr", rating: 4.8, badge: "Top Pick", url: "https://screamingfrog.co.uk", tags: ["freemium"] },
      { name: "Sitebulb", tagline: "Visual technical audits with AI recommendations", price: "From $14mo", rating: 4.6, badge: null, url: "https://sitebulb.com", tags: ["paid"] },
      { name: "Google Search Console", tagline: "Free indexing and performance data from Google", price: "Free", rating: 4.5, badge: "Best Free Option", url: "https://search.google.com/search-console", tags: ["free"] },
    ],
    "Content Optimisation": [
      { name: "Surfer SEO", tagline: "Optimise existing content with AI scoring", price: "From $89/mo", rating: 4.7, badge: "Top Pick", url: "https://surferseo.com", tags: ["paid"] },
      { name: "Frase", tagline: "Research, write, and optimise content in one tool", price: "From $15mo", rating: 4.5, badge: "Best Value", url: "https://frase.io", tags: ["paid"] },
      { name: "Claude", tagline: "Rewrite and improve existing content for SEO", price: "Free / $20mo", rating: 4.7, badge: "Best for Rewrites", url: "https://claude.ai", tags: ["freemium"] },
    ]
  },
  ecommerce: {
    "Build an Online Store": [
      { name: "Shopify", tagline: "The world's leading ecommerce platform", price: "From $29/mo", rating: 4.8, badge: "Top Pick", url: "https://shopify.com", tags: ["paid"] },
      { name: "Squarespace", tagline: "Beautiful stores with built-in AI tools", price: "From $16/mo", rating: 4.5, badge: "Best for Design", url: "https://squarespace.com", tags: ["paid"] },
      { name: "Wix eCommerce", tagline: "Drag-and-drop store builder for beginners", price: "From $17/mo", rating: 4.4, badge: "Easiest to Use", url: "https://wix.com", tags: ["paid", "beginner-friendly"] },
      { name: "WooCommerce", tagline: "Free open-source store on WordPress", price: "Free (hosting extra)", rating: 4.4, badge: "Best Free Option", url: "https://woocommerce.com", tags: ["free"] },
    ],
    "Product Descriptions": [
      { name: "Claude", tagline: "Compelling, SEO-rich product copy at scale", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium"] },
      { name: "Jasper", tagline: "Product description templates for any category", price: "From $49/mo", rating: 4.6, badge: null, url: "https://jasper.ai", tags: ["paid"] },
      { name: "Copysmith", tagline: "Bulk product description generation for catalogues", price: "From $19/mo", rating: 4.4, badge: "Best for Bulk", url: "https://copysmith.ai", tags: ["paid"] },
    ],
    "Pricing & Inventory": [
      { name: "Prisync", tagline: "Track competitor prices and automate repricing", price: "From $59/mo", rating: 4.5, badge: "Top Pick", url: "https://prisync.com", tags: ["paid"] },
      { name: "Shopify Analytics", tagline: "Built-in inventory and pricing intelligence", price: "Included with Shopify", rating: 4.5, badge: "Best if on Shopify", url: "https://shopify.com/analytics", tags: ["paid"] },
      { name: "Linnworks", tagline: "Multi-channel inventory management with AI", price: "Custom", rating: 4.4, badge: "Best for Multi-Channel", url: "https://linnworks.com", tags: ["enterprise"] },
    ],
    "Ad Campaigns": [
      { name: "Meta Advantage+", tagline: "AI-automated Facebook and Instagram ads", price: "Ad spend only", rating: 4.6, badge: "Top Pick", url: "https://facebook.com/business", tags: ["freemium"] },
      { name: "Google Performance Max", tagline: "AI runs ads across all Google channels", price: "Ad spend only", rating: 4.5, badge: null, url: "https://ads.google.com", tags: ["freemium"] },
      { name: "AdCreative.ai", tagline: "Generate high-converting ad creatives with AI", price: "From $21/mo", rating: 4.5, badge: "Best for Creative", url: "https://adcreative.ai", tags: ["paid"] },
    ],
    "Customer Reviews & UGC": [
      { name: "Yotpo", tagline: "Reviews, loyalty, and UGC platform for ecommerce", price: "Free / custom", rating: 4.6, badge: "Top Pick", url: "https://yotpo.com", tags: ["freemium"] },
      { name: "Okendo", tagline: "Review collection with rich media and Q&A", price: "From $19/mo", rating: 4.5, badge: "Best for Shopify", url: "https://okendo.io", tags: ["paid"] },
      { name: "Trustpilot", tagline: "Widely trusted third-party review platform", price: "Free / $259mo", rating: 4.4, badge: "Most Trusted", url: "https://trustpilot.com", tags: ["freemium"] },
    ]
  },
  customerresearch: {
    "Surveys & Feedback": [
      { name: "Typeform", tagline: "Conversational surveys with high completion rates", price: "Free / $25mo", rating: 4.7, badge: "Top Pick", url: "https://typeform.com", tags: ["freemium", "beginner-friendly"] },
      { name: "Tally", tagline: "Free Typeform alternative with unlimited responses", price: "Free / $29mo", rating: 4.6, badge: "Best Free Option", url: "https://tally.so", tags: ["freemium"] },
      { name: "SurveyMonkey", tagline: "Enterprise survey tool with AI analysis", price: "Free / $25mo", rating: 4.4, badge: "Best Enterprise", url: "https://surveymonkey.com", tags: ["freemium"] },
    ],
    "User Interviews": [
      { name: "Maze", tagline: "AI-moderated user research at scale", price: "Free / $99mo", rating: 4.6, badge: "Top Pick", url: "https://maze.co", tags: ["freemium"] },
      { name: "Dovetail", tagline: "Store, analyse, and share user research insights", price: "Free / $29mo", rating: 4.7, badge: "Best for Teams", url: "https://dovetailapp.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Synthesise interview transcripts into key themes", price: "Free / $20mo", rating: 4.8, badge: "Best for Analysis", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Customer Personas": [
      { name: "Claude", tagline: "Build rich personas from research or descriptions", price: "Free / $20mo", rating: 4.9, badge: "Top Pick", url: "https://claude.ai", tags: ["freemium", "beginner-friendly"] },
      { name: "Xtensio", tagline: "Beautiful persona templates and collaboration", price: "Free / $8mo", rating: 4.4, badge: null, url: "https://xtensio.com", tags: ["freemium"] },
      { name: "HubSpot Make My Persona", tagline: "Free guided persona builder", price: "Free", rating: 4.3, badge: "Best Free Option", url: "https://hubspot.com/make-my-persona", tags: ["free"] },
    ],
    "Sentiment Analysis": [
      { name: "Brandwatch", tagline: "Enterprise social listening and sentiment tracking", price: "Custom", rating: 4.6, badge: "Top Pick", url: "https://brandwatch.com", tags: ["enterprise"] },
      { name: "MonkeyLearn", tagline: "No-code sentiment analysis on any text data", price: "Free / $299mo", rating: 4.4, badge: null, url: "https://monkeylearn.com", tags: ["freemium"] },
      { name: "Claude", tagline: "Analyse customer feedback tone and themes", price: "Free / $20mo", rating: 4.8, badge: "Best Value", url: "https://claude.ai", tags: ["freemium"] },
    ],
    "Churn Analysis": [
      { name: "ChurnZero", tagline: "Predict and prevent customer churn in real-time", price: "Custom", rating: 4.5, badge: "Top Pick", url: "https://churnzero.com", tags: ["enterprise"] },
      { name: "Baremetrics", tagline: "SaaS metrics with churn breakdown and forecasting", price: "From $108/mo", rating: 4.5, badge: "Best for SaaS", url: "https://baremetrics.com", tags: ["paid"] },
      { name: "Claude", tagline: "Identify churn patterns from your data and reports", price: "Free / $20mo", rating: 4.7, badge: "Best Value", url: "https://claude.ai", tags: ["freemium"] },
    ]
  }
};

const BADGE_COLORS = {
  "Top Pick": "bg-amber-400 text-amber-900",
  "Best for Teams": "bg-blue-500 text-white",
  "Best Value": "bg-emerald-500 text-white",
  "Most Powerful": "bg-purple-500 text-white",
  "Easiest to Use": "bg-sky-400 text-white",
  "Best Free Option": "bg-teal-500 text-white",
  "Best for SEO": "bg-orange-500 text-white",
  "Best for Sales": "bg-rose-500 text-white",
  "Best for LinkedIn": "bg-blue-600 text-white",
  "Best for Meetings": "bg-indigo-500 text-white",
  "Best for Fiction": "bg-fuchsia-500 text-white",
  "Best for Devs": "bg-slate-600 text-white",
  "Best for Complex Code": "bg-violet-600 text-white",
  "Best Enterprise": "bg-slate-700 text-white",
  "Best No-Code": "bg-pink-500 text-white",
  "Best for Outreach": "bg-cyan-600 text-white",
  "Commercially Safe": "bg-green-600 text-white",
  "Best for Editors": "bg-red-500 text-white",
  "Best for Academic": "bg-blue-700 text-white",
  "Best Tutor": "bg-amber-500 text-amber-950",
  "Best Hands-On": "bg-lime-600 text-white",
  "Best for Networking": "bg-indigo-600 text-white",
  "Best for Quick Research": "bg-cyan-500 text-white",
  "Best for Trends": "bg-orange-600 text-white",
  "Best for Speaking": "bg-green-500 text-white",
  "Best for Beginners": "bg-sky-500 text-white",
  "Best for Audio": "bg-purple-600 text-white",
  "Most Comprehensive": "bg-violet-600 text-white",
  "Best for Repurposing": "bg-pink-600 text-white",
  "Best for Social": "bg-rose-500 text-white",
  "Best for Story": "bg-fuchsia-600 text-white",
  "Best for Startups": "bg-orange-500 text-white",
  "Best for Rewrites": "bg-lime-600 text-white",
  "Best for Design": "bg-pink-500 text-white",
  "Best for Bulk": "bg-slate-500 text-white",
  "Best if on Shopify": "bg-green-700 text-white",
  "Best for Multi-Channel": "bg-teal-600 text-white",
  "Best for Creative": "bg-purple-500 text-white",
  "Most Trusted": "bg-blue-700 text-white",
  "Best for Analysis": "bg-indigo-700 text-white",
  "Best for Designers": "bg-violet-500 text-white",
  "Best for SaaS": "bg-cyan-700 text-white",
};

const TAG_COLORS = {
  "free": "bg-emerald-100 text-emerald-800",
  "freemium": "bg-blue-100 text-blue-800",
  "paid": "bg-slate-100 text-slate-700",
  "enterprise": "bg-purple-100 text-purple-800",
  "beginner-friendly": "bg-amber-100 text-amber-800",
  "teams": "bg-indigo-100 text-indigo-800",
  "seo": "bg-orange-100 text-orange-800",
};

// ── Storage key helper ────────────────────────────────────────────────────────
function reviewKey(toolName) {
  return `reviews:${toolName.replace(/[^a-zA-Z0-9]/g, "_")}`;
}

// ── Curated star display ──────────────────────────────────────────────────────
function StarDisplay({ rating, size = "sm" }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const sz = size === "sm" ? "text-sm" : "text-base";
  return (
    <div className="flex items-center gap-0.5">
      {Array(full).fill("★").map((s, i) => <span key={`f${i}`} className={`${sz} text-amber-400`}>{s}</span>)}
      {half && <span className={`${sz} text-amber-300`}>★</span>}
      {Array(empty).fill("☆").map((s, i) => <span key={`e${i}`} className={`${sz} text-slate-300`}>{s}</span>)}
      <span className="text-xs font-semibold text-slate-500 ml-1">{rating}</span>
    </div>
  );
}

// ── Interactive star picker ───────────────────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          className="text-2xl transition-transform hover:scale-110 focus:outline-none"
          style={{ color: n <= (hovered || value) ? "#f59e0b" : "#d1d5db" }}
        >★</button>
      ))}
    </div>
  );
}

// ── Review modal ──────────────────────────────────────────────────────────────
function ReviewModal({ tool, onClose, onSaved }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit() {
    if (rating === 0) { setError("Please pick a star rating."); return; }
    if (comment.trim().length < 10) { setError("Please write at least 10 characters."); return; }
    setSaving(true);
    setError("");
    try {
      const key = reviewKey(tool.name);
      let existing = [];
      try {
        const res = await window.storage.get(key, true);
        if (res?.value) existing = JSON.parse(res.value);
      } catch {}
      const newReview = {
        id: Date.now(),
        rating,
        comment: comment.trim(),
        name: name.trim() || "Anonymous",
        date: new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })
      };
      const updated = [newReview, ...existing].slice(0, 50);
      await window.storage.set(key, JSON.stringify(updated), true);
      onSaved(updated);
      onClose();
    } catch (e) {
      setError("Could not save review. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,12,40,0.7)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #1e1b4b, #4c1d95)" }}>
          <div>
            <div className="text-white font-bold text-sm">Review {tool.name}</div>
            <div className="text-indigo-300 text-xs">Help the community pick the right tool</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg transition-all">×</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Star picker */}
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 block">Your Rating</label>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Your Name <span className="font-normal text-slate-400">(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Sarah"
              maxLength={40}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all"
            />
          </div>

          {/* Comment */}
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Your Review</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={`What did you use ${tool.name} for? What worked well or didn't?`}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all resize-none"
            />
            <div className="text-right text-xs text-slate-300 mt-0.5">{comment.length}/500</div>
          </div>

          {error && <p className="text-red-500 text-xs font-medium">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-50 hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
            {saving ? "Saving…" : "Submit Review"}
          </button>
          <p className="text-center text-xs text-slate-400">Reviews are shared publicly with all visitors</p>
        </div>
      </div>
    </div>
  );
}

// ── Reviews drawer ────────────────────────────────────────────────────────────
function ReviewsDrawer({ tool, reviews, onClose, onAddReview }) {
  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const dist = [5,4,3,2,1].map(n => ({
    n,
    count: reviews.filter(r => r.rating === n).length,
    pct: reviews.length ? Math.round(reviews.filter(r => r.rating === n).length / reviews.length * 100) : 0
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ background: "rgba(15,12,40,0.7)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: "85vh" }}>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #1e1b4b, #4c1d95)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-white text-xs font-black">
              {tool.name.slice(0,2).toUpperCase()}
            </div>
            <div>
              <div className="text-white font-bold text-sm">{tool.name} — Community Reviews</div>
              <div className="text-indigo-300 text-xs">{reviews.length} review{reviews.length !== 1 ? "s" : ""} from real users</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg">×</button>
        </div>

        {/* Summary bar */}
        {reviews.length > 0 && (
          <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0 flex items-center gap-6">
            <div className="text-center">
              <div className="text-4xl font-black text-slate-800">{avg}</div>
              <StarDisplay rating={parseFloat(avg)} />
              <div className="text-xs text-slate-400 mt-1">{reviews.length} reviews</div>
            </div>
            <div className="flex-1 space-y-1">
              {dist.map(d => (
                <div key={d.n} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-4 text-right">{d.n}</span>
                  <span className="text-amber-400 text-xs">★</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-amber-400 transition-all duration-500"
                      style={{ width: `${d.pct}%` }} />
                  </div>
                  <span className="text-xs text-slate-400 w-6">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" style={{ minHeight: 0 }}>
          {reviews.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-4xl mb-3">💬</div>
              <p className="text-slate-500 font-semibold text-sm">No reviews yet</p>
              <p className="text-slate-400 text-xs mt-1">Be the first to share your experience</p>
            </div>
          ) : (
            reviews.map(r => (
              <div key={r.id} className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: "linear-gradient(135deg, #6366f1, #a855f7)" }}>
                      {r.name.slice(0,1).toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold text-slate-700">{r.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1,2,3,4,5].map(n => (
                        <span key={n} className="text-sm" style={{ color: n <= r.rating ? "#f59e0b" : "#e2e8f0" }}>★</span>
                      ))}
                    </div>
                    <span className="text-xs text-slate-400">{r.date}</span>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{r.comment}</p>
              </div>
            ))
          )}
        </div>

        {/* CTA */}
        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <button onClick={onAddReview}
            className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
            ✍️ Write a Review
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tool Card (with ratings) ──────────────────────────────────────────────────
function ToolCard({ tool, index }) {
  const [hovered, setHovered] = useState(false);
  const [reviews, setReviews] = useState(null); // null = not loaded yet
  const [showDrawer, setShowDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load community reviews from shared storage on mount
  useEffect(() => {
    async function load() {
      try {
        const res = await window.storage.get(reviewKey(tool.name), true);
        setReviews(res?.value ? JSON.parse(res.value) : []);
      } catch {
        setReviews([]);
      }
    }
    load();
  }, [tool.name]);

  const communityAvg = reviews?.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  function handleCardClick(e) {
    // Don't navigate if clicking a button inside the card
    if (e.target.closest("button")) return;
    window.open(tool.url, "_blank");
  }

  return (
    <>
      {showDrawer && (
        <ReviewsDrawer
          tool={tool}
          reviews={reviews || []}
          onClose={() => setShowDrawer(false)}
          onAddReview={() => { setShowDrawer(false); setShowModal(true); }}
        />
      )}
      {showModal && (
        <ReviewModal
          tool={tool}
          onClose={() => setShowModal(false)}
          onSaved={updated => { setReviews(updated); }}
        />
      )}

      <div
        className="relative rounded-2xl border transition-all duration-300 overflow-hidden"
        style={{
          background: hovered ? "linear-gradient(135deg, #f8faff 0%, #eef4ff 100%)" : "#ffffff",
          borderColor: hovered ? "#6366f1" : "#e2e8f0",
          boxShadow: hovered ? "0 8px 32px rgba(99,102,241,0.15)" : "0 2px 8px rgba(0,0,0,0.06)",
          transform: hovered ? "translateY(-2px)" : "translateY(0)",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {tool.badge && (
          <div className={`absolute top-3 right-3 text-xs font-bold px-2 py-0.5 rounded-full ${BADGE_COLORS[tool.badge] || "bg-slate-200 text-slate-700"}`}>
            {tool.badge}
          </div>
        )}

        {/* Main clickable area */}
        <div className="p-5 cursor-pointer" onClick={handleCardClick}>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
              {tool.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 pr-16">
              <h3 className="font-bold text-slate-800 text-base leading-tight">{tool.name}</h3>
              <p className="text-slate-500 text-xs mt-0.5 leading-snug">{tool.tagline}</p>
            </div>
          </div>

          {/* Editor score + price */}
          <div className="flex items-center justify-between mt-3">
            <div>
              <div className="text-xs text-slate-400 mb-0.5">Editor score</div>
              <StarDisplay rating={tool.rating} />
            </div>
            <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-lg">{tool.price}</span>
          </div>

          {/* Community rating */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            {reviews === null ? (
              <div className="text-xs text-slate-300 animate-pulse">Loading community rating…</div>
            ) : communityAvg ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Community score</div>
                  <div className="flex items-center gap-1.5">
                    <StarDisplay rating={parseFloat(communityAvg)} />
                    <span className="text-xs text-slate-400">({reviews.length})</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">No community reviews yet — be the first</div>
            )}
          </div>

          <div className="flex flex-wrap gap-1 mt-3">
            {tool.tags.map(tag => (
              <span key={tag} className={`text-xs px-2 py-0.5 rounded-full font-medium ${TAG_COLORS[tag] || "bg-gray-100 text-gray-600"}`}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Action buttons — Visit always visible */}
        <div className="px-5 pb-4 flex gap-2">
          <a href={tool.url} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex-1 py-2 rounded-xl text-xs font-bold text-center text-white transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
            Visit {tool.name} →
          </a>
          <button
            onClick={() => setShowDrawer(true)}
            className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
            {reviews?.length ? `💬 ${reviews.length}` : "💬"}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-all">
            ✍️
          </button>
        </div>
      </div>
    </>
  );
}

// ── AI Search helpers ─────────────────────────────────────────────────────────

function buildToolIndex() {
  const index = [];
  for (const [catId, subs] of Object.entries(TOOLS_DB)) {
    for (const [subName, tools] of Object.entries(subs)) {
      for (const tool of tools) {
        index.push({ ...tool, catId, subName });
      }
    }
  }
  return index;
}

const TOOL_INDEX = buildToolIndex();

function getToolSummary() {
  // Send a condensed version to keep the prompt size manageable
  return TOOL_INDEX.map(t =>
    `${t.name} | ${t.subName} | ${t.tagline} | ${t.price} | tags: ${t.tags.join(",")}`
  ).join("\n");
}

async function askClaude(userMessage, history) {
  const systemPrompt = `You are an expert AI tool advisor embedded in AIToolPicker — a curated directory of AI tools.

Your job: given a user's plain-English description of what they want to accomplish, recommend the most relevant tools from the database below. Be concise, friendly, and specific. Explain WHY each tool suits their situation.

Return your response as JSON with this exact shape:
{
  "intro": "one sentence acknowledging what the user wants to do",
  "recommendations": [
    {
      "toolName": "exact tool name from database",
      "reason": "1-2 sentences why this tool fits their need",
      "startTip": "one concrete first step to get started"
    }
  ],
  "followUp": "one short question to help narrow down further if needed"
}

Return ONLY valid JSON. No markdown, no preamble, no explanation outside the JSON.

TOOL DATABASE:
${getToolSummary()}`;

  const messages = [
    ...history,
    { role: "user", content: userMessage }
  ];

  try {
    const response = await fetch("/api/claude", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 1000,
        system: systemPrompt,
        messages
      })
    });

    if (!response.ok) {
      const errText = await response.text();
      console.error("API error:", response.status, errText);
      return {
        intro: "Sorry, I ran into an issue. Please try again in a moment.",
        recommendations: [],
        followUp: ""
      };
    }

    const data = await response.json();
    const text = data.content?.find(b => b.type === "text")?.text || "{}";
    try {
      return JSON.parse(text.replace(/```json|```/g, "").trim());
    } catch {
      console.error("JSON parse error:", text);
      return {
        intro: "I found some tools for you but had trouble formatting them. Please try again.",
        recommendations: [],
        followUp: ""
      };
    }
  } catch (err) {
    console.error("Fetch error:", err);
    return {
      intro: "Connection error. Please check your internet and try again.",
      recommendations: [],
      followUp: ""
    };
  }
}

// ── AI Panel component ────────────────────────────────────────────────────────

function AiSearchPanel({ onClose }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]); // [{role, content}]
  const [responses, setResponses] = useState([]); // [{userMsg, result}]
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [responses, loading]);

  const SUGGESTIONS = [
    "I want to make YouTube videos for my small business",
    "I need to automate my email follow-ups",
    "I want to build a website with no coding experience",
    "I run an online store and want to write better product descriptions",
    "I need to transcribe and summarise meetings",
    "I want to understand why customers are leaving"
  ];

  async function handleSubmit(msg) {
    const message = (msg || input).trim();
    if (!message || loading) return;
    setInput("");
    setLoading(true);

    const newHistory = [...history, { role: "user", content: message }];
    const result = await askClaude(message, history);
    const assistantReply = JSON.stringify(result);

    setHistory([...newHistory, { role: "assistant", content: assistantReply }]);
    setResponses(prev => [...prev, { userMsg: message, result }]);
    setLoading(false);
  }

  function getToolData(toolName) {
    return TOOL_INDEX.find(t => t.name.toLowerCase() === toolName.toLowerCase());
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ background: "rgba(15,12,40,0.7)", backdropFilter: "blur(6px)" }}>
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        style={{ background: "#ffffff", maxHeight: "90vh" }}>

        {/* Panel header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #1e1b4b, #4c1d95)" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-lg">✨</div>
            <div>
              <div className="text-white font-bold text-sm">AI Tool Advisor</div>
              <div className="text-indigo-300 text-xs">Describe what you want to do — I'll find the right tools</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg transition-all">×</button>
        </div>

        {/* Conversation area */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" style={{ minHeight: 0 }}>

          {/* Empty state */}
          {responses.length === 0 && !loading && (
            <div>
              <p className="text-slate-500 text-sm mb-4 text-center">Try one of these, or type your own:</p>
              <div className="grid grid-cols-1 gap-2">
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => handleSubmit(s)}
                    className="text-left text-sm px-4 py-3 rounded-xl border-2 border-slate-100 hover:border-indigo-300 hover:bg-indigo-50 text-slate-600 transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation turns */}
          {responses.map((turn, i) => (
            <div key={i} className="space-y-4">
              {/* User bubble */}
              <div className="flex justify-end">
                <div className="max-w-xs px-4 py-3 rounded-2xl rounded-tr-sm text-sm font-medium text-white"
                  style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
                  {turn.userMsg}
                </div>
              </div>

              {/* AI response */}
              <div className="space-y-3">
                {turn.result.intro && (
                  <p className="text-slate-700 text-sm font-medium">{turn.result.intro}</p>
                )}

                {(turn.result.recommendations || []).map((rec, j) => {
                  const toolData = getToolData(rec.toolName);
                  return (
                    <div key={j} className="rounded-2xl border border-slate-100 overflow-hidden"
                      style={{ background: "linear-gradient(135deg, #f8faff, #f3f0ff)" }}>
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #6366f1, #a855f7)" }}>
                            {rec.toolName.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-800 text-sm">{rec.toolName}</span>
                              {toolData?.badge && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${BADGE_COLORS[toolData.badge] || "bg-slate-200 text-slate-700"}`}>
                                  {toolData.badge}
                                </span>
                              )}
                              {toolData?.price && (
                                <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded-lg">{toolData.price}</span>
                              )}
                            </div>
                            <p className="text-slate-600 text-xs mt-1 leading-relaxed">{rec.reason}</p>
                            <div className="mt-2 flex items-start gap-1.5">
                              <span className="text-emerald-500 text-xs mt-0.5">▶</span>
                              <p className="text-emerald-700 text-xs font-medium">{rec.startTip}</p>
                            </div>
                          </div>
                        </div>
                        {toolData?.url && (
                          <a href={toolData.url} target="_blank" rel="noopener noreferrer"
                            className="mt-3 flex items-center justify-center gap-1 w-full py-2 rounded-xl text-xs font-bold text-indigo-600 border-2 border-indigo-100 hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                            Visit {rec.toolName} →
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}

                {turn.result.followUp && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    <span className="text-amber-500 text-sm">💬</span>
                    <p className="text-amber-800 text-xs font-medium">{turn.result.followUp}</p>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center gap-3 text-slate-400 text-sm">
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div key={i} className="w-2 h-2 rounded-full bg-indigo-400"
                    style={{ animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
              Finding the best tools for you…
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="px-4 pb-4 pt-2 border-t border-slate-100 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              placeholder="Describe what you want to do…"
              className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all"
            />
            <button
              onClick={() => handleSubmit()}
              disabled={loading || !input.trim()}
              className="px-5 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              Ask
            </button>
          </div>
          {responses.length > 0 && (
            <button onClick={() => { setResponses([]); setHistory([]); }}
              className="mt-2 text-xs text-slate-400 hover:text-slate-600 transition-colors w-full text-center">
              Start a new search
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

// ── Monetisation helpers ──────────────────────────────────────────────────────

// Featured tools config — add tool names here to mark as featured
const SPONSORED_TOOLS = new Set(["Jasper", "Semrush", "Shopify"]);
const FEATURED_BY_SUB = {
  "Blog & Articles": "Jasper",
  "Keyword Research": "Semrush",
  "Build an Online Store": "Shopify",
};

async function trackClick(toolName, subName, isSponsored) {
  try {
    const dateKey = new Date().toISOString().slice(0, 10);
    const clickKey = `clicks:${dateKey}`;
    let existing = {};
    try {
      const res = await window.storage.get(clickKey, true);
      if (res?.value) existing = JSON.parse(res.value);
    } catch {}
    const key = `${toolName}||${subName}`;
    existing[key] = (existing[key] || 0) + 1;
    await window.storage.set(clickKey, JSON.stringify(existing), true);

    // Also track totals
    let totals = {};
    try {
      const res = await window.storage.get("clicks:totals", true);
      if (res?.value) totals = JSON.parse(res.value);
    } catch {}
    totals[key] = (totals[key] || 0) + 1;
    await window.storage.set("clicks:totals", JSON.stringify(totals), true);
  } catch {}
}

// ── Featured Tool Banner ──────────────────────────────────────────────────────
function FeaturedBanner({ subName, onTrackClick }) {
  const featuredName = FEATURED_BY_SUB[subName];
  if (!featuredName) return null;

  const tool = TOOL_INDEX.find(t => t.name === featuredName && t.subName === subName);
  if (!tool) return null;

  return (
    <div className="mb-5 rounded-2xl overflow-hidden border-2 border-amber-300"
      style={{ background: "linear-gradient(135deg, #fffbeb, #fef3c7)" }}>
      <div className="px-4 py-1.5 flex items-center gap-2"
        style={{ background: "linear-gradient(90deg, #f59e0b, #f97316)" }}>
        <span className="text-white text-xs font-black tracking-wide">⭐ FEATURED TOOL</span>
        <span className="text-amber-100 text-xs">· Editor's top recommendation</span>
      </div>
      <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-sm"
            style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
            {tool.name.slice(0,2).toUpperCase()}
          </div>
          <div>
            <div className="font-black text-slate-800 text-base flex items-center gap-2">
              {tool.name}
              <span className="text-xs bg-amber-400 text-amber-900 font-bold px-2 py-0.5 rounded-full">Editor's Pick</span>
            </div>
            <div className="text-slate-600 text-sm">{tool.tagline}</div>
            <div className="text-indigo-600 text-xs font-semibold mt-0.5">{tool.price}</div>
          </div>
        </div>
        <a href={tool.url} target="_blank" rel="noopener noreferrer"
          onClick={() => onTrackClick(tool.name, subName, true)}
          className="px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
          Visit {tool.name} →
        </a>
      </div>
    </div>
  );
}

// ── Enquiries Tab (used inside Admin) ────────────────────────────────────────
function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(true);

  const PACKAGE_LABELS = {
    featured_sub: "Featured Placement ($99–$299/mo)",
    featured_badge: "Editor's Pick Badge ($49–$99/mo)",
    category: "Category Takeover ($499+/mo)",
    custom: "Custom / Let's Talk"
  };

  useEffect(() => {
    async function load() {
      try {
        const res = await window.storage.get("advertise:enquiries", true);
        setEnquiries(res?.value ? JSON.parse(res.value) : []);
      } catch {
        setEnquiries([]);
      }
      setLoading(false);
    }
    load();
  }, []);

  async function dismiss(id) {
    const updated = enquiries.filter(e => e.id !== id);
    setEnquiries(updated);
    await window.storage.set("advertise:enquiries", JSON.stringify(updated), true);
  }

  if (loading) return <div className="text-slate-400 text-sm text-center py-8 animate-pulse">Loading enquiries…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-700 text-sm">Advertising Enquiries</h3>
        <span className="text-xs bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{enquiries.length} new</span>
      </div>

      {enquiries.length === 0 ? (
        <div className="text-center py-10">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-slate-400 text-sm font-semibold">No enquiries yet</p>
          <p className="text-slate-300 text-xs mt-1">Advertising enquiries from tool owners will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {enquiries.map(e => (
            <div key={e.id} className="p-4 rounded-2xl border-2 border-amber-100 bg-amber-50">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-black text-slate-800 text-sm">{e.name}</span>
                    {e.company && <span className="text-xs text-slate-500">· {e.company}</span>}
                    <span className="text-xs text-slate-400">{e.date}</span>
                  </div>
                  <div className="text-xs bg-amber-200 text-amber-900 font-bold px-2 py-0.5 rounded-full inline-block mb-2">
                    {PACKAGE_LABELS[e.package] || e.package}
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-slate-600 text-xs"><strong>Email:</strong> <a href={`mailto:${e.email}`} className="text-indigo-500 hover:underline">{e.email}</a></p>
                    <p className="text-slate-600 text-xs"><strong>Tool:</strong> <a href={e.toolUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline">{e.toolUrl}</a></p>
                    {e.message && <p className="text-slate-500 text-xs italic mt-1">"{e.message}"</p>}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <a href={`mailto:${e.email}?subject=Re: Advertising on AIToolPicker&body=Hi ${e.name},%0D%0A%0D%0AThanks for your interest in advertising on AIToolPicker.`}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white text-center"
                    style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
                    Reply
                  </a>
                  <button onClick={() => dismiss(e.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500 transition-all">
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Submissions Tab (used inside Admin) ───────────────────────────────────────
function SubmissionsTab() {
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await window.storage.get("submissions:pending", true);
        setSubmissions(res?.value ? JSON.parse(res.value) : []);
      } catch {
        setSubmissions([]);
      }
      setLoading(false);
    }
    load();
  }, []);

  async function dismiss(id) {
    const updated = submissions.filter(s => s.id !== id);
    setSubmissions(updated);
    await window.storage.set("submissions:pending", JSON.stringify(updated), true);
  }

  if (loading) return <div className="text-slate-400 text-sm text-center py-8 animate-pulse">Loading submissions…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-700 text-sm">Pending Tool Submissions</h3>
        <span className="text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">{submissions.length} pending</span>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-10">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-slate-400 text-sm font-semibold">No submissions yet</p>
          <p className="text-slate-300 text-xs mt-1">When visitors submit tools they'll appear here for your review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(s => (
            <div key={s.id} className="p-4 rounded-2xl border-2 border-slate-100 bg-slate-50">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-black text-slate-800 text-sm">{s.name}</span>
                    <span className="text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">
                      {CATEGORIES.find(c => c.id === s.category)?.label || s.category}
                    </span>
                    <span className="text-xs text-slate-400">{s.date}</span>
                  </div>
                  <p className="text-slate-600 text-xs mb-1">{s.tagline}</p>
                  {s.price && <p className="text-indigo-600 text-xs font-semibold mb-1">{s.price}</p>}
                  {s.notes && <p className="text-slate-500 text-xs italic mb-1">"{s.notes}"</p>}
                  {s.submitterEmail && <p className="text-slate-400 text-xs">From: {s.submitterEmail}</p>}
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    className="text-indigo-500 text-xs hover:underline">{s.url}</a>
                </div>
                <div className="flex flex-col gap-2">
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white text-center"
                    style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
                    Visit Site
                  </a>
                  <button onClick={() => dismiss(s.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500 transition-all">
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-slate-400 text-center pt-2">To add a tool to the live site, add it to the TOOLS_DB in App.jsx and redeploy. Then dismiss it here.</p>
        </div>
      )}
    </div>
  );
}

// ── Admin Dashboard ───────────────────────────────────────────────────────────
const ADMIN_PASSWORD = "admin123"; // Change this before deploying!

function AdminDashboard({ onClose }) {
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [clicks, setClicks] = useState({});
  const [loading, setLoading] = useState(false);
  const [freshTab, setFreshTab] = useState("clicks");
  const [freshnessCategory, setFreshnessCategory] = useState("write");
  const [freshnessResult, setFreshnessResult] = useState(null);
  const [freshnessLoading, setFreshnessLoading] = useState(false);

  async function login() {
    if (pwInput === ADMIN_PASSWORD) {
      setAuthed(true);
      loadData();
    } else {
      setPwError("Incorrect password.");
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      const res = await window.storage.get("clicks:totals", true);
      setClicks(res?.value ? JSON.parse(res.value) : {});
    } catch {}
    setLoading(false);
  }

  async function runFreshnessCheck() {
    setFreshnessLoading(true);
    setFreshnessResult(null);
    const catTools = Object.entries(TOOLS_DB[freshnessCategory] || {}).flatMap(([sub, tools]) =>
      tools.map(t => `${t.name} (${sub}): price listed as "${t.price}", tagline: "${t.tagline}"`)
    ).join("\n");

    try {
      const response = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: `You are an AI tool database curator. Review these tool listings and flag any that likely have outdated pricing, changed names, or may have shut down based on your knowledge. Be specific about what to check.

Return JSON: { "flags": [ { "toolName": "...", "issue": "...", "suggestion": "..." } ], "summary": "one sentence" }

TOOLS TO REVIEW:
${catTools}`
          }]
        })
      });
      const data = await response.json();
      const text = data.content?.find(b => b.type === "text")?.text || "{}";
      setFreshnessResult(JSON.parse(text.replace(/```json|```/g, "").trim()));
    } catch {
      setFreshnessResult({ flags: [], summary: "Could not connect to AI. Check your deployment environment." });
    }
    setFreshnessLoading(false);
  }

  // Sort clicks into leaderboard
  const leaderboard = Object.entries(clicks)
    .map(([key, count]) => { const [name, sub] = key.split("||"); return { name, sub, count }; })
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const totalClicks = Object.values(clicks).reduce((s, n) => s + n, 0);
  const sponsoredClicks = Object.entries(clicks)
    .filter(([key]) => SPONSORED_TOOLS.has(key.split("||")[0]))
    .reduce((s, [, n]) => s + n, 0);

  if (!authed) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(15,12,40,0.85)", backdropFilter: "blur(8px)" }}>
        <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="px-6 py-5 text-center"
            style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b)" }}>
            <div className="text-3xl mb-2">🔒</div>
            <div className="text-white font-black text-lg">Admin Dashboard</div>
            <div className="text-slate-400 text-xs mt-1">AIToolPicker owner access only</div>
          </div>
          <div className="p-6 space-y-4">
            <input type="password" value={pwInput} onChange={e => setPwInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && login()}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm outline-none transition-all" />
            {pwError && <p className="text-red-500 text-xs">{pwError}</p>}
            <button onClick={login}
              className="w-full py-3 rounded-xl font-bold text-sm text-white"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              Login
            </button>
            <button onClick={onClose} className="w-full text-center text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,12,40,0.85)", backdropFilter: "blur(8px)" }}>
      <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl my-4 overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b)" }}>
          <div>
            <div className="text-white font-black text-base">⚙️ Admin Dashboard</div>
            <div className="text-slate-400 text-xs">AIToolPicker — Owner View</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg">×</button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-px bg-slate-100">
          {[
            { label: "Total Clicks", value: totalClicks, color: "text-indigo-600" },
            { label: "Featured Clicks", value: sponsoredClicks, color: "text-amber-600" },
            { label: "Tools Tracked", value: Object.keys(clicks).length, color: "text-emerald-600" },
          ].map(s => (
            <div key={s.label} className="bg-white px-6 py-4 text-center">
              <div className={`text-3xl font-black ${s.color}`}>{s.value}</div>
              <div className="text-xs text-slate-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100">
          {[["clicks", "📊 Click Stats"], ["freshness", "🤖 AI Freshness Check"], ["sponsored", "⭐ Featured Config"], ["submissions", "📬 Submissions"], ["enquiries", "💼 Ad Enquiries"]].map(([id, label]) => (
            <button key={id} onClick={() => setFreshTab(id)}
              className={`flex-1 py-3 text-xs font-bold transition-all ${freshTab === id ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-6">

          {/* Click Stats Tab */}
          {freshTab === "clicks" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-700 text-sm">Top Clicked Tools (All Time)</h3>
                <button onClick={loadData} className="text-xs text-indigo-500 hover:underline">Refresh</button>
              </div>
              {loading ? (
                <div className="text-slate-400 text-sm text-center py-8 animate-pulse">Loading click data…</div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-slate-400 text-sm">No clicks tracked yet.</p>
                  <p className="text-slate-300 text-xs mt-1">Clicks will appear here once visitors use the site.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-white"
                        style={{ background: i === 0 ? "#f59e0b" : i === 1 ? "#94a3b8" : i === 2 ? "#b45309" : "#6366f1" }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800 text-sm">{item.name}</span>
                          {SPONSORED_TOOLS.has(item.name) && (
                            <span className="text-xs bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded-full">Featured</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">{item.sub}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-black text-indigo-600 text-sm">{item.count}</div>
                        <div className="text-xs text-slate-400">clicks</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI Freshness Tab */}
          {freshTab === "freshness" && (
            <div>
              <p className="text-slate-500 text-sm mb-4">Select a category and run an AI check to flag tools with potentially outdated pricing, changed names, or that may have shut down.</p>
              <div className="flex gap-3 mb-4">
                <select value={freshnessCategory} onChange={e => setFreshnessCategory(e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm outline-none">
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
                <button onClick={runFreshnessCheck} disabled={freshnessLoading}
                  className="px-5 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-50 transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
                  {freshnessLoading ? "Checking…" : "🤖 Run Check"}
                </button>
              </div>

              {freshnessLoading && (
                <div className="text-center py-8 text-slate-400 text-sm animate-pulse">AI is reviewing tool listings…</div>
              )}

              {freshnessResult && (
                <div className="space-y-3">
                  <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100 text-sm text-indigo-700">
                    <strong>Summary:</strong> {freshnessResult.summary}
                  </div>
                  {freshnessResult.flags?.length === 0 ? (
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-sm text-emerald-700 text-center">
                      ✅ No issues found — all listings look up to date!
                    </div>
                  ) : (
                    freshnessResult.flags?.map((flag, i) => (
                      <div key={i} className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-500 text-base mt-0.5">⚠️</span>
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{flag.toolName}</div>
                            <div className="text-slate-600 text-xs mt-0.5">{flag.issue}</div>
                            <div className="text-indigo-600 text-xs mt-1 font-medium">💡 {flag.suggestion}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Enquiries Tab */}
          {freshTab === "enquiries" && (
            <EnquiriesTab />
          )}

          {/* Submissions Tab */}
          {freshTab === "submissions" && (
            <SubmissionsTab />
          )}

          {/* Sponsored Config Tab */}
          {freshTab === "sponsored" && (
            <div>
              <p className="text-slate-500 text-sm mb-4">These tools currently have featured placement. To add or remove tools, update the <code className="bg-slate-100 px-1 rounded text-xs">SPONSORED_TOOLS</code> and <code className="bg-slate-100 px-1 rounded text-xs">FEATURED_BY_SUB</code> constants in the code.</p>
              <div className="space-y-2 mb-6">
                {[...SPONSORED_TOOLS].map(name => {
                  const featuredIn = Object.entries(FEATURED_BY_SUB).filter(([, v]) => v === name).map(([k]) => k);
                  return (
                    <div key={name} className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-black"
                        style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
                        {name.slice(0,2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-slate-800 text-sm">{name}</div>
                        {featuredIn.length > 0 && <div className="text-xs text-amber-600">Featured in: {featuredIn.join(", ")}</div>}
                      </div>
                      <span className="text-xs bg-amber-400 text-amber-900 font-bold px-2 py-0.5 rounded-full">Active</span>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
                <strong className="text-slate-700">Pricing guide for featured slots:</strong>
                <ul className="mt-2 space-y-1">
                  <li>• Featured banner (per subcategory/month): <strong className="text-indigo-600">$99–$299/mo</strong></li>
                  <li>• Featured badge only (per tool/month): <strong className="text-indigo-600">$49–$99/mo</strong></li>
                  <li>• Category takeover (all subcategories): <strong className="text-indigo-600">$499+/mo</strong></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

// ── Small Business Starter Pack Modal ────────────────────────────────────────
const STARTER_PACK = [
  {
    emoji: "✍️",
    name: "Claude",
    url: "https://claude.ai",
    role: "Your AI Business Assistant",
    why: "Write emails, proposals, social posts, job ads, customer replies and more — just describe what you need in plain English. The single most useful AI tool for any small business owner.",
    price: "Free to start · $20/mo for full access",
    tag: "Start here",
    tagColor: "bg-emerald-500 text-white",
    time: "5 minutes to first result"
  },
  {
    emoji: "🎨",
    name: "Canva AI",
    url: "https://canva.com",
    role: "Design Without a Designer",
    why: "Create logos, social posts, flyers, presentations and ads without any design experience. The AI features generate entire designs from a text description — professional results in minutes.",
    price: "Free · Pro from $15/mo",
    tag: "Best free option",
    tagColor: "bg-blue-500 text-white",
    time: "10 minutes to first design"
  },
  {
    emoji: "⚡",
    name: "Zapier",
    url: "https://zapier.com",
    role: "Automate Your Repetitive Tasks",
    why: "Connect your apps so they work together automatically — when someone fills a form, send them an email; when you get a payment, update your spreadsheet. No code needed. Saves hours every week.",
    price: "Free for 5 automations · From $20/mo",
    tag: "Biggest time saver",
    tagColor: "bg-orange-500 text-white",
    time: "30 minutes to first automation"
  },
  {
    emoji: "📊",
    name: "Notion AI",
    url: "https://notion.so",
    role: "Run Your Business in One Place",
    why: "Docs, tasks, notes, databases and meeting summaries — all in one workspace with AI built in. Replace a dozen scattered tools with one organised hub your whole team can use.",
    price: "Free · Plus from $10/mo",
    tag: "Best for organisation",
    tagColor: "bg-indigo-500 text-white",
    time: "1 hour to set up your workspace"
  },
  {
    emoji: "📱",
    name: "Buffer",
    url: "https://buffer.com",
    role: "Social Media on Autopilot",
    why: "Write, schedule and publish posts across Facebook, Instagram, LinkedIn and more from one place. AI suggests captions and best posting times. Spend 1 hour a week instead of every day.",
    price: "Free for 3 channels · From $18/mo",
    tag: "Best for social media",
    tagColor: "bg-pink-500 text-white",
    time: "20 minutes to schedule first posts"
  },
  {
    emoji: "🎙️",
    name: "Otter.ai",
    url: "https://otter.ai",
    role: "Never Take Meeting Notes Again",
    why: "Joins your Zoom or Google Meet calls, transcribes everything in real time and produces a summary with action items. Free up your attention during meetings and never lose a key decision again.",
    price: "Free for 300 mins/mo · From $10/mo",
    tag: "Instant time saver",
    tagColor: "bg-teal-500 text-white",
    time: "2 minutes to connect"
  },
];

function StarterPackModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,12,40,0.75)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden my-4">

        {/* Header */}
        <div className="px-8 py-6 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #064e3b, #065f46, #047857)" }}>
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-emerald-300 text-xs font-black uppercase tracking-widest mb-2">🚀 Free Guide</div>
              <h2 className="text-white font-black text-2xl mb-2" style={{ letterSpacing: "-0.02em" }}>
                Small Business<br />AI Starter Pack
              </h2>
              <p className="text-emerald-200 text-sm max-w-md">
                6 AI tools that will save you time and money — chosen specifically for small business owners who are new to AI. Start free, see results today.
              </p>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg flex-shrink-0 ml-4">×</button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[
              { value: "6", label: "Essential Tools" },
              { value: "Free", label: "To Get Started" },
              { value: "Hours", label: "Saved Per Week" },
            ].map(s => (
              <div key={s.label} className="bg-white bg-opacity-10 rounded-xl px-3 py-2 text-center">
                <div className="text-white font-black text-lg">{s.value}</div>
                <div className="text-emerald-300 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Intro */}
        <div className="px-8 py-4 bg-emerald-50 border-b border-emerald-100">
          <p className="text-emerald-800 text-xs leading-relaxed">
            <strong>How to use this guide:</strong> You don't need all 6 tools at once. Start with Claude — it's free and immediately useful. Add one more tool per week as you get comfortable. Within a month you'll have a lean, AI-powered business operation.
          </p>
        </div>

        {/* Tool list */}
        <div className="px-6 py-6 space-y-4">
          {STARTER_PACK.map((tool, i) => (
            <div key={tool.name} className="rounded-2xl border-2 border-slate-100 overflow-hidden hover:border-emerald-200 transition-all">
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Number + emoji */}
                  <div className="flex-shrink-0 text-center">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl mb-1">
                      {tool.emoji}
                    </div>
                    <div className="text-xs font-black text-slate-300">#{i + 1}</div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-black text-slate-800 text-base">{tool.name}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tool.tagColor}`}>{tool.tag}</span>
                    </div>
                    <div className="text-xs font-bold text-emerald-700 mb-1">{tool.role}</div>
                    <p className="text-slate-500 text-xs leading-relaxed mb-2">{tool.why}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded-lg">{tool.price}</span>
                      <span className="text-xs text-slate-400">⏱ {tool.time}</span>
                    </div>
                  </div>
                </div>

                {/* Visit button */}
                <a href={tool.url} target="_blank" rel="noopener noreferrer"
                  className="mt-3 flex items-center justify-center gap-1 w-full py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #059669, #047857)" }}>
                  Get started with {tool.name} →
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="px-6 pb-6">
          <div className="p-5 rounded-2xl text-center"
            style={{ background: "linear-gradient(135deg, #f5f3ff, #fdf4ff)", border: "2px dashed #c4b5fd" }}>
            <p className="text-indigo-700 text-sm font-bold mb-1">Not sure which to try first?</p>
            <p className="text-indigo-500 text-xs mb-3">Our AI advisor can match you to the right tool based on your specific business situation.</p>
            <button onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              ✨ Ask the AI Advisor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Legal Modal (Terms of Service + Privacy Policy) ──────────────────────────
function LegalModal({ page, onClose }) {
  const EFFECTIVE_DATE = "17 March 2026";
  const CONTACT_EMAIL = "hello@aitoolpicker.ai";
  const SITE_URL = "https://aitoolpicker.ai";
  const SITE_NAME = "AIToolPicker";

  const TERMS = [
    {
      title: "1. Acceptance of Terms",
      body: `By accessing or using ${SITE_NAME} (${SITE_URL}), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the site. These terms apply to all visitors, users, and anyone who submits content or enquiries through the site.`
    },
    {
      title: "2. What AIToolPicker Is",
      body: `${SITE_NAME} is an independently curated directory of AI tools. We provide information about third-party AI tools and services to help users find tools suited to their needs. We are not affiliated with, endorsed by, or officially connected to any of the tools listed unless explicitly stated. Tool listings represent our editorial opinion and are not a guarantee of quality, suitability, or availability.`
    },
    {
      title: "3. Accuracy of Information",
      body: `We make reasonable efforts to keep tool listings accurate and up to date, including pricing, features, and availability. However, AI tools change frequently. We cannot guarantee that all information is current or error-free. Always verify pricing and features directly with the tool provider before making purchasing decisions. Nothing on this site constitutes professional, legal, or financial advice.`
    },
    {
      title: "4. User Submissions",
      body: `When you submit a tool for review, leave a community review, or send an advertising enquiry, you agree that: (a) the information you provide is accurate and not misleading; (b) you have the right to submit the content; (c) your submission does not infringe any third-party rights; (d) we may use your submission to improve and operate the site. We reserve the right to accept, reject, or remove any submission at our sole discretion.`
    },
    {
      title: "5. Community Reviews",
      body: `Reviews submitted by users represent personal opinions and experiences. ${SITE_NAME} does not verify the accuracy of user reviews. We reserve the right to remove reviews that are defamatory, false, abusive, spam, or otherwise inappropriate. By submitting a review, you grant us a non-exclusive, royalty-free licence to display that review on the site.`
    },
    {
      title: "6. Featured Placements and Advertising",
      body: `Some tools may appear in featured positions on the site. Featured placements may be editorially selected or paid for by tool providers. Paid placements are clearly labelled. Payment for a featured placement does not guarantee any particular ranking, traffic, or outcome. Advertising fees are non-refundable once a placement has been live for 7 or more days. We reserve the right to decline or remove any paid placement that we determine is misleading, inappropriate, or inconsistent with our editorial standards.`
    },
    {
      title: "7. Affiliate Links",
      body: `Some links on ${SITE_NAME} may be affiliate links. This means we may earn a commission if you click through and make a purchase, at no additional cost to you. Affiliate relationships do not influence our editorial ratings or recommendations. We only include affiliate links for tools we would recommend regardless of any commercial relationship.`
    },
    {
      title: "8. Intellectual Property",
      body: `All content on ${SITE_NAME} including text, design, layout, and editorial curation is owned by or licensed to ${SITE_NAME} and is protected by New Zealand and international copyright law. You may not reproduce, republish, scrape, or redistribute our content without prior written permission. Tool names, logos, and trademarks belong to their respective owners.`
    },
    {
      title: "9. Disclaimer of Warranties",
      body: `${SITE_NAME} is provided on an "as is" basis without warranties of any kind. We do not warrant that the site will be uninterrupted, error-free, or free of harmful components. To the extent permitted by New Zealand law, we disclaim all implied warranties including fitness for a particular purpose and merchantability.`
    },
    {
      title: "10. Limitation of Liability",
      body: `To the maximum extent permitted by the Consumer Guarantees Act 1993 and the Fair Trading Act 1986 (New Zealand), ${SITE_NAME} shall not be liable for any indirect, incidental, or consequential damages arising from your use of the site, including reliance on tool listings or reviews. Our total liability to you shall not exceed NZD $100.`
    },
    {
      title: "11. Third-Party Links",
      body: `${SITE_NAME} contains links to third-party websites and tools. We are not responsible for the content, privacy practices, or terms of those sites. Clicking a link to a third-party site is at your own risk.`
    },
    {
      title: "12. Changes to Terms",
      body: `We may update these Terms of Service from time to time. We will update the effective date at the top of this page. Continued use of the site after changes are posted constitutes your acceptance of the updated terms.`
    },
    {
      title: "13. Governing Law",
      body: `These terms are governed by the laws of New Zealand. Any disputes shall be subject to the exclusive jurisdiction of the New Zealand courts. Nothing in these terms limits your rights under the Consumer Guarantees Act 1993 or the Fair Trading Act 1986.`
    },
    {
      title: "14. Contact",
      body: `For any questions about these terms, please contact us at ${CONTACT_EMAIL}.`
    }
  ];

  const PRIVACY = [
    {
      title: "1. Who We Are",
      body: `${SITE_NAME} (${SITE_URL}) is operated by an individual based in New Zealand. We are committed to protecting your personal information in accordance with the Privacy Act 2020 (New Zealand) and, where applicable, the General Data Protection Regulation (GDPR) for visitors from the European Economic Area.`
    },
    {
      title: "2. What Information We Collect",
      body: `We collect the following types of information: (a) Information you provide voluntarily — including your name, email address, tool submission details, advertising enquiry details, and community review content when you use our submission forms; (b) Anonymous usage data — including which tools are clicked and which categories are viewed. This data contains no personally identifiable information; (c) We do not use cookies for tracking or advertising purposes. We do not use Google Analytics or any third-party tracking scripts.`
    },
    {
      title: "3. How We Use Your Information",
      body: `We use the information you provide to: (a) Review and process tool submissions; (b) Respond to advertising enquiries; (c) Display community reviews on the site; (d) Improve the site based on anonymous usage patterns; (e) Communicate with you if you have requested a response. We do not sell, rent, or trade your personal information to third parties. We do not use your information for unsolicited marketing.`
    },
    {
      title: "4. How We Store Your Information",
      body: `Information submitted through our forms is stored using Vercel's edge storage infrastructure. Vercel is a US-based provider that complies with GDPR and maintains appropriate security standards. Anonymous click tracking data is stored in aggregate form with no link to individual users. We retain personal information only as long as necessary to fulfil the purpose for which it was collected, and delete it upon request.`
    },
    {
      title: "5. Your Rights (New Zealand — Privacy Act 2020)",
      body: `Under the Privacy Act 2020, you have the right to: (a) Access any personal information we hold about you; (b) Request correction of inaccurate personal information; (c) Request deletion of your personal information; (d) Make a complaint to the Office of the Privacy Commissioner if you believe we have breached your privacy rights. To exercise any of these rights, contact us at ${CONTACT_EMAIL}.`
    },
    {
      title: "6. Your Rights (EEA Visitors — GDPR)",
      body: `If you are located in the European Economic Area, you also have the right to: (a) Data portability — receive your data in a portable format; (b) Restrict processing of your data; (c) Object to processing based on legitimate interests; (d) Lodge a complaint with your local supervisory authority. Our lawful basis for processing your data is legitimate interest (improving the site) and consent (when you voluntarily submit information through our forms).`
    },
    {
      title: "7. Community Reviews",
      body: `Reviews you submit are displayed publicly on the site alongside your chosen display name. If you wish to have a review removed, contact us at ${CONTACT_EMAIL} and we will remove it within 5 business days.`
    },
    {
      title: "8. Children's Privacy",
      body: `${SITE_NAME} is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has submitted information to us, please contact us and we will delete it promptly.`
    },
    {
      title: "9. Third-Party Services",
      body: `We use Vercel for hosting and edge storage, and Cloudflare for DNS and email routing. These providers may process data as part of delivering the service. Links to third-party tools on this site are governed by those tools' own privacy policies — we encourage you to review them before using any tool.`
    },
    {
      title: "10. Changes to This Policy",
      body: `We may update this Privacy Policy from time to time. We will update the effective date at the top of this page. We encourage you to review this page periodically.`
    },
    {
      title: "11. Contact and Complaints",
      body: `For any privacy-related questions or requests, contact us at ${CONTACT_EMAIL}. If you are unsatisfied with our response, you may contact the Office of the New Zealand Privacy Commissioner at privacy.org.nz or call 0800 803 909.`
    }
  ];

  const content = page === "terms" ? TERMS : PRIVACY;
  const title = page === "terms" ? "Terms of Service" : "Privacy Policy";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,12,40,0.75)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden my-4">

        {/* Header */}
        <div className="px-8 py-5 flex items-center justify-between sticky top-0"
          style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b)" }}>
          <div>
            <div className="text-white font-black text-lg">{title}</div>
            <div className="text-slate-400 text-xs">AIToolPicker · Effective {EFFECTIVE_DATE}</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg flex-shrink-0">×</button>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-6">
          {content.map((section, i) => (
            <div key={i}>
              <h3 className="font-bold text-slate-800 text-sm mb-2">{section.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{section.body}</p>
            </div>
          ))}

          <div className="pt-4 border-t border-slate-100 text-center">
            <p className="text-slate-400 text-xs">Questions? Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-500 hover:underline">{CONTACT_EMAIL}</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Advertise Page Modal ──────────────────────────────────────────────────────
function AdvertiseModal({ onClose, onSubmitTool }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", toolUrl: "", package: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    if (!form.name.trim()) { setError("Please enter your name."); return; }
    if (!form.email.trim()) { setError("Please enter your email."); return; }
    if (!form.toolUrl.trim()) { setError("Please enter your tool URL."); return; }
    if (!form.package) { setError("Please select a package."); return; }
    setSaving(true);
    setError("");
    try {
      let enquiries = [];
      try {
        const res = await window.storage.get("advertise:enquiries", true);
        if (res?.value) enquiries = JSON.parse(res.value);
      } catch {}
      const newEnquiry = {
        id: Date.now(),
        ...form,
        date: new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }),
        status: "new"
      };
      enquiries = [newEnquiry, ...enquiries].slice(0, 200);
      await window.storage.set("advertise:enquiries", JSON.stringify(enquiries), true);
      setDone(true);
    } catch {
      setError("Could not submit. Please try again.");
    }
    setSaving(false);
  }

  const PACKAGES = [
    { id: "featured_sub", label: "Featured Placement", price: "$99–$299/mo", desc: "Your tool appears as the top recommended pick in a specific subcategory with an Editor's Pick banner." },
    { id: "featured_badge", label: "Editor's Pick Badge", price: "$49–$99/mo", desc: "Your tool card gets an Editor's Pick badge across all categories it appears in." },
    { id: "category", label: "Category Takeover", price: "$499+/mo", desc: "Featured placement across all subcategories in an entire category. Maximum visibility." },
    { id: "custom", label: "Custom / Let's Talk", price: "Custom", desc: "Not sure what you need? Tell us about your tool and we'll suggest the best fit." },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,12,40,0.75)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden my-4">

        {/* Header */}
        <div className="px-8 py-6 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b, #4c1d95)" }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-amber-400 text-xs font-black uppercase tracking-widest mb-2">Grow Your User Base</div>
              <h2 className="text-white font-black text-2xl mb-2" style={{ letterSpacing: "-0.02em" }}>Advertise on AIToolPicker</h2>
              <p className="text-indigo-200 text-sm max-w-md">Reach thousands of people actively searching for AI tools. Our visitors are ready to try new tools — they just need to find yours.</p>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg flex-shrink-0 ml-4">×</button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[
              { value: "200+", label: "AI Tools Listed" },
              { value: "14", label: "Categories" },
              { value: "Global", label: "Audience Reach" },
            ].map(s => (
              <div key={s.label} className="bg-white bg-opacity-10 rounded-xl px-3 py-2 text-center">
                <div className="text-white font-black text-lg">{s.value}</div>
                <div className="text-indigo-300 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h3 className="font-black text-slate-800 text-lg mb-2">Enquiry received!</h3>
            <p className="text-slate-500 text-sm mb-2">Thanks for your interest. We'll be in touch at <strong>{form.email}</strong> within 1–2 business days.</p>
            <p className="text-slate-400 text-xs mb-6">In the meantime you can also reach us directly at <strong>hello@aitoolpicker.ai</strong></p>
            <button onClick={onClose}
              className="px-6 py-3 rounded-xl font-bold text-sm text-white"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              Back to AIToolPicker
            </button>
          </div>
        ) : (
          <div className="p-6">

            {/* Packages */}
            <h3 className="font-black text-slate-800 text-base mb-4">Choose a Package</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {PACKAGES.map(pkg => (
                <button key={pkg.id}
                  onClick={() => update("package", pkg.id)}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${form.package === pkg.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300 bg-white"}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-bold text-slate-800 text-sm">{pkg.label}</span>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full flex-shrink-0 ${form.package === pkg.id ? "bg-indigo-500 text-white" : "bg-amber-100 text-amber-800"}`}>{pkg.price}</span>
                  </div>
                  <p className="text-slate-500 text-xs leading-relaxed">{pkg.desc}</p>
                </button>
              ))}
            </div>

            {/* Contact form */}
            <h3 className="font-black text-slate-800 text-base mb-4">Your Details</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Your Name <span className="text-red-400">*</span></label>
                  <input type="text" value={form.name} onChange={e => update("name", e.target.value)}
                    placeholder="e.g. Sarah Johnson"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Company / Tool Name</label>
                  <input type="text" value={form.company} onChange={e => update("company", e.target.value)}
                    placeholder="e.g. Jasper AI"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Email Address <span className="text-red-400">*</span></label>
                <input type="email" value={form.email} onChange={e => update("email", e.target.value)}
                  placeholder="e.g. hello@yourtool.com"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Tool Website <span className="text-red-400">*</span></label>
                <input type="url" value={form.toolUrl} onChange={e => update("toolUrl", e.target.value)}
                  placeholder="e.g. https://yourtool.com"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Message <span className="font-normal text-slate-400">(optional)</span></label>
                <textarea value={form.message} onChange={e => update("message", e.target.value)}
                  placeholder="Tell us about your tool, target audience, or any questions you have…"
                  rows={3} maxLength={500}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all resize-none" />
              </div>
            </div>

            {error && <p className="text-red-500 text-xs font-medium mt-2">{error}</p>}

            <button onClick={handleSubmit} disabled={saving}
              className="w-full mt-4 py-3 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-50 hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}>
              {saving ? "Sending…" : "Send Enquiry →"}
            </button>

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-slate-400">Or email us directly: <a href="mailto:hello@aitoolpicker.ai" className="text-indigo-500 font-semibold hover:underline">hello@aitoolpicker.ai</a></p>
              <button onClick={() => { onClose(); onSubmitTool(); }}
                className="text-xs text-indigo-500 font-semibold hover:underline">
                Just want to submit your tool for free? →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Submit a Tool Modal ───────────────────────────────────────────────────────
function SubmitToolModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", url: "", category: "", tagline: "", price: "", submitterEmail: "", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    if (!form.name.trim()) { setError("Please enter the tool name."); return; }
    if (!form.url.trim()) { setError("Please enter the tool URL."); return; }
    if (!form.category) { setError("Please select a category."); return; }
    if (!form.tagline.trim()) { setError("Please enter a short description."); return; }
    setSaving(true);
    setError("");
    try {
      // Save submission to shared storage for admin to review
      let submissions = [];
      try {
        const res = await window.storage.get("submissions:pending", true);
        if (res?.value) submissions = JSON.parse(res.value);
      } catch {}
      const newSubmission = {
        id: Date.now(),
        ...form,
        date: new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }),
        status: "pending"
      };
      submissions = [newSubmission, ...submissions].slice(0, 200);
      await window.storage.set("submissions:pending", JSON.stringify(submissions), true);
      setDone(true);
    } catch {
      setError("Could not submit. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,12,40,0.7)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden my-4">

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #1e1b4b, #4c1d95)" }}>
          <div>
            <div className="text-white font-bold text-sm">🛠️ Submit an AI Tool</div>
            <div className="text-indigo-300 text-xs">Help the community discover great tools</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white flex items-center justify-center text-lg">×</button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h3 className="font-black text-slate-800 text-lg mb-2">Thank you!</h3>
            <p className="text-slate-500 text-sm mb-6">Your submission has been received. We review all tools manually and will add qualifying tools within a few days.</p>
            <button onClick={onClose}
              className="px-6 py-3 rounded-xl font-bold text-sm text-white"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">

            {/* Tool name */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Tool Name <span className="text-red-400">*</span></label>
              <input type="text" value={form.name} onChange={e => update("name", e.target.value)}
                placeholder="e.g. Notion AI"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
            </div>

            {/* URL */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Website URL <span className="text-red-400">*</span></label>
              <input type="url" value={form.url} onChange={e => update("url", e.target.value)}
                placeholder="e.g. https://notion.so"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
            </div>

            {/* Category */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Best Category <span className="text-red-400">*</span></label>
              <select value={form.category} onChange={e => update("category", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all bg-white">
                <option value="">Select a category…</option>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>

            {/* Tagline */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Short Description <span className="text-red-400">*</span></label>
              <input type="text" value={form.tagline} onChange={e => update("tagline", e.target.value)}
                placeholder="e.g. All-in-one workspace with AI writing and summarisation"
                maxLength={100}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
              <div className="text-right text-xs text-slate-300 mt-0.5">{form.tagline.length}/100</div>
            </div>

            {/* Pricing */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Pricing</label>
              <input type="text" value={form.price} onChange={e => update("price", e.target.value)}
                placeholder="e.g. Free / $10mo or From $29/mo"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Why should we add it? <span className="font-normal text-slate-400">(optional)</span></label>
              <textarea value={form.notes} onChange={e => update("notes", e.target.value)}
                placeholder="What makes this tool stand out? Who is it best for?"
                rows={2} maxLength={300}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all resize-none" />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Your Email <span className="font-normal text-slate-400">(optional — we'll notify you when added)</span></label>
              <input type="email" value={form.submitterEmail} onChange={e => update("submitterEmail", e.target.value)}
                placeholder="e.g. hello@example.com"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all" />
            </div>

            {error && <p className="text-red-500 text-xs font-medium">{error}</p>}

            <p className="text-center text-xs text-slate-400">By submitting you agree to our <button type="button" onClick={onClose} className="text-indigo-500 hover:underline">Terms of Service</button> and <button type="button" onClick={onClose} className="text-indigo-500 hover:underline">Privacy Policy</button>. Your email will only be used to notify you when your tool is added.</p>

            <button onClick={handleSubmit} disabled={saving}
              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-50 hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              {saving ? "Submitting…" : "Submit Tool for Review"}
            </button>

            <p className="text-center text-xs text-slate-400">All submissions are manually reviewed before being added. We typically review within 2–3 days.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SEO: Update page title and meta description dynamically ──────────────────
function useSEO(selectedCategory, selectedSub) {
  useEffect(() => {
    const base = "AIToolPicker";
    const cat = CATEGORIES.find(c => c.id === selectedCategory);
    if (selectedSub) {
      document.title = `Best AI Tools for ${selectedSub} — ${base}`;
      document.querySelector('meta[name="description"]')?.setAttribute("content",
        `Compare the best AI tools for ${selectedSub}. Curated rankings with pricing, ratings and real user reviews. Find your perfect AI tool today.`
      );
    } else if (cat) {
      document.title = `Best AI Tools for ${cat.label} — ${base}`;
      document.querySelector('meta[name="description"]')?.setAttribute("content",
        `Find the best AI tools for ${cat.label.toLowerCase()}. Compare top-rated options with pricing, free tiers and user reviews. Updated regularly.`
      );
    } else {
      document.title = `${base} — Find the Best AI Tool for Any Job`;
      document.querySelector('meta[name="description"]')?.setAttribute("content",
        `Find the best AI tool for exactly what you need. Browse and compare 200+ curated AI tools across writing, images, video, SEO, automation, coding and more.`
      );
    }
  }, [selectedCategory, selectedSub]);
}

export default function App() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [search, setSearch] = useState("");
  const [filterFree, setFilterFree] = useState(false);
  const [filterBeginner, setFilterBeginner] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showAdvertise, setShowAdvertise] = useState(false);
  const [showStarterPack, setShowStarterPack] = useState(false);
  const [legalPage, setLegalPage] = useState(null); // "terms" | "privacy" | null
  const resultsRef = useRef(null);

  // SEO: update page title on navigation
  useSEO(selectedCategory, selectedSub);

  const category = CATEGORIES.find(c => c.id === selectedCategory);

  const tools = selectedCategory && selectedSub && TOOLS_DB[selectedCategory]?.[selectedSub]
    ? TOOLS_DB[selectedCategory][selectedSub].filter(t => {
        if (filterFree && !t.tags.includes("free") && !t.tags.includes("freemium")) return false;
        if (filterBeginner && !t.tags.includes("beginner-friendly")) return false;
        if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.tagline.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      })
    : [];

  // Global search — searches across ALL tools when on homepage
  const globalResults = search.trim().length > 1 && !selectedSub
    ? TOOL_INDEX.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.tagline.toLowerCase().includes(search.toLowerCase()) ||
        t.subName.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 12)
    : [];

  useEffect(() => {
    if (selectedSub && resultsRef.current) {
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [selectedSub]);

  // Keyboard shortcut: Ctrl+Shift+A opens admin
  useEffect(() => {
    function handler(e) {
      if (e.ctrlKey && e.shiftKey && e.key === "A") setShowAdmin(true);
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", minHeight: "100vh", background: "linear-gradient(160deg, #f0f4ff 0%, #fafafa 50%, #f5f0ff 100%)" }}>

      {showAI && <AiSearchPanel onClose={() => setShowAI(false)} />}
      {showAdmin && <AdminDashboard onClose={() => setShowAdmin(false)} />}
      {showSubmit && <SubmitToolModal onClose={() => setShowSubmit(false)} />}
      {showAdvertise && <AdvertiseModal onClose={() => setShowAdvertise(false)} onSubmitTool={() => setShowSubmit(true)} />}
      {showStarterPack && <StarterPackModal onClose={() => setShowStarterPack(false)} />}
      {legalPage && <LegalModal page={legalPage} onClose={() => setLegalPage(null)} />}

      {/* ── Navbar ── */}
      <nav style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)" }}
        className="px-6 py-4 relative">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo — clickable home */}
          <button
            onClick={() => { setSelectedCategory(null); setSelectedSub(null); setSearch(""); setFilterFree(false); setFilterBeginner(false); }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm text-white"
              style={{ background: "linear-gradient(135deg, #a78bfa, #f9a8d4)" }}>
              AI
            </div>
            <span className="text-white font-black text-base tracking-tight">AIToolPicker</span>
          </button>

          {/* Nav links */}
          <div className="flex items-center gap-5">
            <button onClick={() => setShowAI(true)}
              className="hidden sm:block text-indigo-300 hover:text-white text-xs font-semibold transition-colors">
              ✨ AI Advisor
            </button>
            <button onClick={() => setShowStarterPack(true)}
              className="hidden sm:block text-emerald-300 hover:text-white text-xs font-semibold transition-colors">
              🚀 Small Business Guide
            </button>
            <button onClick={() => setShowSubmit(true)}
              className="hidden sm:block text-indigo-300 hover:text-white text-xs font-semibold transition-colors">
              🛠️ Submit a Tool
            </button>
            <button onClick={() => setShowAdvertise(true)}
              className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)", color: "#1e1b4b" }}>
              ⭐ Advertise
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)" }}
        className="px-6 pb-10 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="relative max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white bg-opacity-10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 border border-white border-opacity-20">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            14 categories · 150+ use cases · curated daily
          </div>
          <h1 className="text-4xl font-black text-white mb-3" style={{ letterSpacing: "-0.03em" }}>
            Find the right AI tool,<br />
            <span style={{ background: "linear-gradient(90deg, #a78bfa, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              for exactly what you need.
            </span>
          </h1>
          <p className="text-indigo-200 text-base max-w-lg mx-auto mb-6">
            Browse by category — or let our AI advisor find the perfect tool just by describing what you want to do.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-lg mx-auto">
            <button onClick={() => setShowAI(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #a78bfa, #f9a8d4)", color: "#1e1b4b" }}>
              ✨ Ask AI to find my tool
            </button>
            <input type="text" placeholder="Search by name or task…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-auto flex-1 px-4 py-3 rounded-xl text-slate-800 text-sm font-medium outline-none border-2 border-transparent focus:border-indigo-400 transition-all"
              style={{ background: "rgba(255,255,255,0.95)" }} />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-700">
            {selectedCategory && (
              <span className="cursor-pointer text-indigo-500 hover:underline mr-1"
                onClick={() => { setSelectedCategory(null); setSelectedSub(null); }}>
                ← All Categories
              </span>
            )}
            {!selectedCategory && "Browse by category"}
            {category && <span className="text-slate-400"> / {category.icon} {category.label}</span>}
            {selectedSub && <span className="text-slate-400"> / {selectedSub}</span>}
          </h2>
          {!selectedCategory && !search && (
            <p className="text-slate-400 text-xs mt-1">
              Not sure where to start? Try <button onClick={() => setShowAI(true)} className="text-indigo-500 font-semibold hover:underline">asking our AI advisor</button> — or browse popular categories like <button onClick={() => { setSelectedCategory("write"); setSelectedSub(null); }} className="text-indigo-500 font-semibold hover:underline">Writing</button>, <button onClick={() => { setSelectedCategory("images"); setSelectedSub(null); }} className="text-indigo-500 font-semibold hover:underline">Images</button>, or <button onClick={() => { setSelectedCategory("build"); setSelectedSub(null); }} className="text-indigo-500 font-semibold hover:underline">Build Something</button>.
            </p>
          )}
        </div>

        {/* Global Search Results */}
        {globalResults.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  Results for: <span className="text-indigo-600">"{search}"</span>
                </h2>
                <p className="text-slate-400 text-sm">{globalResults.length} tools found across all categories</p>
              </div>
              <button onClick={() => setSearch("")}
                className="text-xs text-slate-400 hover:text-slate-600 underline">
                Clear search
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {globalResults.map((tool, i) => (
                <div key={`${tool.name}-${tool.subName}`}>
                  <div className="mb-1">
                    <span className="text-xs text-indigo-500 font-semibold">
                      {CATEGORIES.find(c => c.id === tool.catId)?.icon} {tool.subName}
                    </span>
                  </div>
                  <div onClick={() => trackClick(tool.name, tool.subName, SPONSORED_TOOLS.has(tool.name))}>
                    <ToolCard tool={tool} index={i} />
                  </div>
                </div>
              ))}
            </div>
            {globalResults.length === 12 && (
              <p className="text-center text-xs text-slate-400 mt-4">Showing top 12 results — try a more specific search to narrow down</p>
            )}
          </div>
        )}

        {/* No results state */}
        {search.trim().length > 1 && !selectedSub && globalResults.length === 0 && (
          <div className="text-center py-12 text-slate-400 mb-8">
            <div className="text-4xl mb-3">🔍</div>
            <p className="font-semibold">No tools found for "{search}"</p>
            <p className="text-sm mt-1">Try a different search term or browse by category below</p>
            <button onClick={() => setShowAI(true)}
              className="mt-4 px-5 py-2.5 rounded-xl font-bold text-sm text-white inline-flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              ✨ Ask AI instead
            </button>
          </div>
        )}

        {/* Small Business Starter Pack Banner — homepage only */}
        {!selectedCategory && !search && (
          <div className="mb-8 rounded-2xl overflow-hidden cursor-pointer group"
            onClick={() => setShowStarterPack(true)}
            style={{ background: "linear-gradient(135deg, #064e3b, #065f46)" }}>
            <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="text-4xl">🚀</div>
                <div>
                  <div className="text-emerald-300 text-xs font-black uppercase tracking-widest mb-0.5">Free Guide</div>
                  <div className="text-white font-black text-lg leading-tight">Small Business AI Starter Pack</div>
                  <div className="text-emerald-300 text-xs mt-0.5">6 tools that save time and money — start free today</div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-white text-xs font-bold bg-white bg-opacity-10 px-4 py-2 rounded-xl group-hover:bg-opacity-20 transition-all">
                  View the guide →
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Category Grid — hidden when global search is active */}
        {!selectedCategory && globalResults.length === 0 && search.trim().length <= 1 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
            {CATEGORIES.map(cat => (
              <button key={cat.id}
                onClick={() => { setSelectedCategory(cat.id); setSelectedSub(null); }}
                className="text-left p-5 rounded-2xl border-2 border-slate-200 bg-white hover:border-indigo-400 hover:shadow-lg transition-all duration-200 group">
                <div className="text-3xl mb-2">{cat.icon}</div>
                <div className="font-bold text-slate-800 text-sm group-hover:text-indigo-700 transition-colors">{cat.label}</div>
                <div className="text-xs text-slate-400 mt-1">{cat.description}</div>
              </button>
            ))}
            <button onClick={() => setShowAI(true)}
              className="text-left p-5 rounded-2xl border-2 border-dashed border-indigo-300 hover:border-indigo-500 hover:shadow-lg transition-all duration-200 group"
              style={{ background: "linear-gradient(135deg, #f5f3ff, #fdf4ff)" }}>
              <div className="text-3xl mb-2">✨</div>
              <div className="font-bold text-indigo-700 text-sm group-hover:text-indigo-900">Not sure? Ask AI</div>
              <div className="text-xs text-indigo-400 mt-1">Describe your goal, get matched instantly</div>
            </button>
          </div>
        )}

        {/* Subcategory grid */}
        {selectedCategory && !selectedSub && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {category.subcategories.map(sub => (
              <button key={sub} onClick={() => setSelectedSub(sub)}
                className="text-left p-4 rounded-2xl border-2 border-slate-200 bg-white hover:border-indigo-400 hover:shadow-md transition-all duration-200 group">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-700 text-sm group-hover:text-indigo-700 transition-colors">{sub}</div>
                  {FEATURED_BY_SUB[sub] && <span className="text-xs bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded-full">Featured</span>}
                </div>
                <div className="text-xs text-slate-400 mt-1">{TOOLS_DB[selectedCategory]?.[sub]?.length || 0} tools</div>
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {selectedSub && (
          <div ref={resultsRef}>
            {/* Featured banner */}
            <FeaturedBanner subName={selectedSub} onTrackClick={trackClick} />

            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  Best tools for: <span className="text-indigo-600">{selectedSub}</span>
                </h2>
                <p className="text-slate-400 text-sm">{tools.length} tools matched</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setFilterFree(!filterFree)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${filterFree ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                  Free / Freemium only
                </button>
                <button onClick={() => setFilterBeginner(!filterBeginner)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${filterBeginner ? "bg-amber-400 text-amber-900 border-amber-400" : "bg-white text-slate-600 border-slate-200 hover:border-amber-400"}`}>
                  Beginner-friendly
                </button>
                <button onClick={() => setShowAI(true)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-all">
                  ✨ Ask AI instead
                </button>
              </div>
            </div>

            {tools.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <div className="text-4xl mb-3">🔍</div>
                <p className="font-semibold">No tools match your filters.</p>
                <button onClick={() => { setFilterFree(false); setFilterBeginner(false); setSearch(""); }}
                  className="mt-3 text-indigo-500 text-sm hover:underline">Clear filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tools.map((tool, i) => (
                  <div key={tool.name} onClick={() => trackClick(tool.name, selectedSub, SPONSORED_TOOLS.has(tool.name))}>
                    <ToolCard tool={tool} index={i} />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 p-5 rounded-2xl bg-indigo-50 border border-indigo-100 text-sm text-indigo-700">
              <strong>💡 Not sure which to start with?</strong> Look for the <span className="font-bold text-amber-700">Top Pick</span> badge — or{" "}
              <button onClick={() => setShowAI(true)} className="underline font-bold hover:text-indigo-900">ask our AI advisor</button> to pick for your exact situation.
            </div>
          </div>
        )}

        {/* About + Trust + SEO Content Block */}
        <div className="mt-16 pt-10 border-t border-slate-200">
          <div className="max-w-3xl mx-auto">

            {/* About section — builds trust for both users and advertisers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="md:col-span-2">
                <h2 className="text-xl font-black text-slate-700 mb-3">About AIToolPicker</h2>
                <p className="text-slate-500 text-sm leading-relaxed mb-3">
                  AIToolPicker is an independently curated directory of AI tools, organised by what you actually want to <em>do</em>. We cut through the noise so you don't have to spend hours comparing options — just tell us your goal and we'll show you what works.
                </p>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Every tool is evaluated on merit. We don't accept payment to inflate ratings. Featured placements are clearly disclosed. Our AI advisor matches you to the right tool from a plain-English description — no technical knowledge needed.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  { icon: "🎯", title: "Intent-first", desc: "Browse by what you want to do, not just category" },
                  { icon: "⭐", title: "Independently rated", desc: "No pay-to-rank. Honest editor scores on every tool" },
                  { icon: "💬", title: "Community reviewed", desc: "Real ratings from real users on every listing" },
                  { icon: "🔄", title: "Regularly updated", desc: "AI freshness checks keep listings accurate" },
                ].map(f => (
                  <div key={f.title} className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">{f.icon}</span>
                    <div>
                      <div className="font-bold text-slate-700 text-xs">{f.title}</div>
                      <div className="text-slate-400 text-xs">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit a Tool CTA */}
            <div className="mb-10 p-6 rounded-2xl text-center"
              style={{ background: "linear-gradient(135deg, #f5f3ff, #fdf4ff)", border: "2px dashed #c4b5fd" }}>
              <div className="text-2xl mb-2">🛠️</div>
              <h3 className="font-black text-indigo-800 text-base mb-1">Know a tool we're missing?</h3>
              <p className="text-indigo-600 text-xs mb-4">We review all submissions and add qualifying tools within a few days.</p>
              <button onClick={() => setShowSubmit(true)}
                className="px-6 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
                Submit a Tool
              </button>
            </div>

            {/* FAQ — feeds Google rich results and AI search answers */}
            <h3 className="text-lg font-bold text-slate-700 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {[
                { q: "What is the best AI tool for writing?", a: "Claude by Anthropic consistently ranks as the top AI writing tool for quality and long-form content. Jasper is best for marketing teams, while Copy.ai excels at fast copy generation." },
                { q: "What is the best free AI tool?", a: "The best free AI tools include Claude, ChatGPT, Canva AI, Ideogram for image generation, and Otter.ai for transcription. Most top tools offer a free tier to get started." },
                { q: "Which AI tool is best for small businesses?", a: "For small businesses: Claude or ChatGPT for writing, Canva AI for design, Buffer for social scheduling, Zapier for automation, and Tidio for customer support." },
                { q: "What is the best AI image generator?", a: "Midjourney is the benchmark for quality. Adobe Firefly is best for commercially safe images. DALL·E 3 offers the most accurate prompt following, and Ideogram is the strongest free option." },
                { q: "How is AIToolPicker different from other AI tool directories?", a: "We organise tools by use case and intent — not just alphabetical lists. Our AI advisor matches you from a plain-English description of your goal, with honest pricing, editor scores, and real community reviews." },
              ].map((faq, i) => (
                <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <h4 className="font-bold text-slate-700 text-sm mb-1">{faq.q}</h4>
                  <p className="text-slate-500 text-xs leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 text-center text-xs text-slate-400">
          <p className="font-semibold text-slate-500 mb-1">AIToolPicker</p>
          <p>Independently curated · Updated regularly · Tools listed on merit, not payment</p>
          <p className="mt-1">© 2026 AIToolPicker. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
            <button onClick={() => setShowStarterPack(true)}
              className="text-emerald-500 hover:text-emerald-700 transition-colors text-xs font-semibold">
              🚀 Small Business Guide
            </button>
            <span className="text-slate-200">·</span>
            <button onClick={() => setShowSubmit(true)}
              className="text-indigo-400 hover:text-indigo-600 transition-colors text-xs font-semibold">
              🛠️ Submit a Tool
            </button>
            <span className="text-slate-200">·</span>
            <button onClick={() => setShowAdvertise(true)}
              className="text-amber-500 hover:text-amber-700 transition-colors text-xs font-semibold">
              ⭐ Advertise With Us
            </button>
            <span className="text-slate-200">·</span>
            <a href="mailto:hello@aitoolpicker.ai"
              className="text-slate-400 hover:text-slate-600 transition-colors text-xs">
              hello@aitoolpicker.ai
            </a>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2 flex-wrap">
            <button onClick={() => setLegalPage("terms")}
              className="text-slate-400 hover:text-slate-600 transition-colors text-xs">
              Terms of Service
            </button>
            <span className="text-slate-200">·</span>
            <button onClick={() => setLegalPage("privacy")}
              className="text-slate-400 hover:text-slate-600 transition-colors text-xs">
              Privacy Policy
            </button>
            <span className="text-slate-200">·</span>
            <button onClick={() => setShowAdmin(true)}
              className="text-slate-300 hover:text-slate-500 transition-colors text-xs">
              Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
